/**
 * VendorsTab.jsx - Full Implementation (Parts 1-4)
 * 
 * Part 1: A1-A6 KPI Strip, D1-D7 Search & Filters, K1-K3 Totals, M1-M10 Business Logic ✅
 * Part 2: E1-E12 Grouped Table, F1-F5 Expandable Row ✅
 * Part 3: G1-G13 Detail Drawer, H1-H9 Add Vendor Modal ✅
 * Part 4: B1-B3 Intelligence Cards, C1-C3 Alert Bar, I1-I8 Log Payment, J1-J4 Review Payment, L1-L3 Auto-Save ✅
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Search, Plus, X, Building2, User, Phone, Mail,
  Calendar, AlertCircle, Utensils, ChevronDown,
  ChevronUp, Edit2, Trash2, Paperclip, FileText,
  ArrowUpDown, MoreHorizontal, ExternalLink, CreditCard,
  CheckCircle, Clock, Eye, Check, Upload,
  Undo2, AlertTriangle, Wallet, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import VendorDetailDrawer from './VendorDetailDrawer';
import AdminPageHeader from './AdminPageHeader';
import OrchestrationIndicator from './event-readiness/OrchestrationIndicator';
import { getModuleAwareness } from '../../data/api';
import LogPaymentModal from './LogPaymentModal';
import ReviewPaymentModal from './ReviewPaymentModal';
import ContractIntelligencePanel from './ContractIntelligencePanel';
import C from '../../data/colors';
import axios from 'axios';
import './vendors-sphere.css';
import {
  VENDOR_CATEGORY_GROUPS,
  VENDOR_CATEGORIES,
  EXPERIENCE_GROUP_ORDER,
  STATUS_CONFIG,
  COLORS,
  formatMoney,
  formatShortDate,
  formatFullDate,
  formatPhone,
  formatPhoneInput,
  daysUntil,
  getVendorPaid,
  getVendorContract,
  getVendorRemaining,
  getLastPayment,
  getPaymentCount,
  getDotSignal,
  getRiskLevel,
  getRiskLabel,
  getVendorExperienceGroup,
  getVendorSearchText,
  sortVendors,
  groupVendorsByExperience,
  suggestVendorTimeline,
  getMissingContractVendors,
  getUpcomingPaymentVendors,
  getUrgentVendor,
  isMissingContract,
  extractContractInsights,
} from '../../lib/vendorInsights';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Constants ─────────────────────────────────────────────────────────────────

const ROLE_CATEGORIES = [
  "Venue", "Catering", "Bar Service", "Rentals",
  "Photography", "Videography", "Flowers", "Stationery", "Decor",
  "DJ", "Band", "Entertainment", "MC",
  "Transportation", "Planner", "Coordinator", "Beauty", "Hair/Makeup", "Cake", "Security", "Other"
];

const VENDOR_STATUSES = ["Considering", "Shortlisted", "Hired", "InProgress", "Completed", "Canceled"];
const PAYMENT_STATUSES = ["NotPaid", "DepositPaid", "PartiallyPaid", "PaidInFull", "Overdue"];

const SORT_OPTIONS = [
  { key: 'due_date', label: 'Due Date' },
  { key: 'balance_desc', label: 'Balance' },
  { key: 'contract_desc', label: 'Contract' },
  { key: 'name_asc', label: 'Name' },
];

const statusColors = {
  Considering: { bg: 'rgba(107,114,128,0.15)', text: '#566173' },
  Shortlisted: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa' },
  Hired: { bg: 'rgba(34,197,94,0.15)', text: '#22c55e' },
  InProgress: { bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' },
  Completed: { bg: 'rgba(5,150,105,0.15)', text: '#10b981' },
  Canceled: { bg: 'rgba(220,53,69,0.15)', text: '#f87171' },
};

// ─── CSS Keyframes for Blinking Dot (E3) ───────────────────────────────────────

const blinkKeyframes = `
@keyframes vendorDotBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
`;

// Inject keyframes into document head
if (typeof document !== 'undefined') {
  const styleId = 'vendor-blink-keyframes';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = blinkKeyframes;
    document.head.appendChild(style);
  }
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  // Page container — the authentic white-sphere canvas (orbs ride in .vsphere-bg behind the cards)
  container: {
    position: 'relative',
    isolation: 'isolate',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #FBFCFE 60%, #F7F9FC 100%)',
    borderRadius: 20,
    padding: '22px 24px 32px',
  },

  // KPI Strip (A1-A6)
  kpiStrip: {
    display: 'flex',
    flexDirection: 'row',
    background: 'linear-gradient(150deg, rgba(255,255,255,0.50), rgba(255,255,255,0.30))',
    backdropFilter: 'blur(28px) saturate(160%) brightness(1.06)',
    WebkitBackdropFilter: 'blur(28px) saturate(160%) brightness(1.06)',
    border: '1px solid rgba(198,167,90,0.24)',
    borderTop: '2px solid #C6A75A',
    borderRadius: 22,
    overflow: 'hidden',
    marginBottom: 18,
    boxShadow: '0px 30px 60px rgba(18,18,18,0.08), 0px 10px 24px rgba(18,18,18,0.05), inset 0px 1px 0px rgba(255,255,255,0.96)',
  },
  kpiCell: {
    flex: 1,
    padding: '18px',
    minHeight: 88,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  kpiCellBorder: {
    borderRight: '1px solid rgba(198,167,90,0.16)',
  },
  kpiLabel: {
    fontSize: 9,
    letterSpacing: '0.95px',
    color: '#4F4838',
    fontWeight: 600,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  kpiValue: {
    fontSize: 21,
    fontWeight: 600,
    color: '#1F1C17',
    letterSpacing: '-0.2px',
  },
  kpiSub: {
    fontSize: 10,
    color: '#5C5446',
    marginTop: 4,
    lineHeight: '14px',
  },
  // Financial trio — visually grouped + emphasized "financial summary"
  kpiValueMoney: {
    fontSize: 23,
    fontWeight: 700,
    color: '#1F1C17',
    letterSpacing: '-0.3px',
  },
  kpiCellFinancial: {
    background: 'linear-gradient(180deg, rgba(198,167,90,0.07), rgba(198,167,90,0.02))',
  },

  // Control Shell (Search + Filters) (D1-D7)
  controlShell: {
    marginBottom: 18,
    borderRadius: 22,
    border: '1px solid rgba(198,167,90,0.22)',
    borderTop: '2px solid #C6A75A',
    background: 'linear-gradient(150deg, rgba(255,255,255,0.50), rgba(255,255,255,0.30))',
    backdropFilter: 'blur(28px) saturate(160%) brightness(1.06)',
    WebkitBackdropFilter: 'blur(28px) saturate(160%) brightness(1.06)',
    padding: '16px 18px',
    boxShadow: '0px 26px 54px rgba(18,18,18,0.07), 0px 8px 20px rgba(18,18,18,0.045), inset 0px 1px 0px rgba(255,255,255,0.94)',
  },
  controlMetaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    padding: '0 2px',
  },
  controlEyebrow: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.9px',
    color: '#4F4838',
  },
  controlMeta: {
    fontSize: 10,
    color: '#5C5446',
    fontWeight: 600,
  },

  // Toolbar
  toolbar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 14,
    flexWrap: 'wrap',
  },
  searchWrapper: {
    flex: 1,
    minWidth: 220,
    position: 'relative',
  },
  searchInput: {
    width: '100%',
    padding: '10px 12px 10px 36px',
    border: '1px solid rgba(198,167,90,0.22)',
    borderRadius: 12,
    fontSize: 13,
    background: C.bgCard,
    color: '#1F1C17',
    boxShadow: '0px 10px 18px rgba(18,18,18,0.03), inset 0px 1px 0px rgba(255,255,255,0.9)',
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#5C5446',
  },

  // Category Filter Chips (D6)
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  chip: {
    minHeight: 38,
    padding: '9px 12px',
    border: '1px solid rgba(198,167,90,0.22)',
    borderRadius: 12,
    background: C.bgCard,
    cursor: 'pointer',
    boxShadow: '0px 10px 18px rgba(18,18,18,0.03), inset 0px 1px 0px rgba(255,255,255,0.9)',
    transition: 'all 0.15s ease',
  },
  chipActive: {
    background: C.statusWarningTint,
    borderColor: 'rgba(198,167,90,0.38)',
  },
  chipText: {
    fontSize: 10,
    color: '#6F685C',
    fontWeight: 700,
    letterSpacing: '0.65px',
    textTransform: 'uppercase',
  },
  chipTextActive: {
    color: '#876B2C',
  },

  // Sort Button (D7)
  sortBtn: {
    minHeight: 38,
    padding: '9px 12px',
    border: '1px solid rgba(198,167,90,0.22)',
    background: C.bgCard,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    boxShadow: '0px 10px 18px rgba(18,18,18,0.03), inset 0px 1px 0px rgba(255,255,255,0.9)',
  },
  sortBtnLabel: {
    fontSize: 10,
    color: '#5C5446',
    fontWeight: 700,
    letterSpacing: '0.75px',
    textTransform: 'uppercase',
  },
  sortBtnText: {
    fontSize: 12,
    color: '#463F34',
    fontWeight: 600,
  },
  sortDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    background: C.bgCard,
    border: '1px solid rgba(198,167,90,0.22)',
    borderRadius: 12,
    boxShadow: '0px 20px 40px rgba(18,18,18,0.12)',
    zIndex: 100,
    minWidth: 140,
    overflow: 'hidden',
  },
  sortOption: {
    padding: '10px 14px',
    fontSize: 12,
    color: '#463F34',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  sortOptionActive: {
    background: C.statusWarningTint,
    fontWeight: 600,
    color: '#876B2C',
  },

  // Filter Dropdowns (D2-D4)
  filterDropdown: {
    padding: '10px 12px',
    border: '1px solid rgba(198,167,90,0.22)',
    borderRadius: 12,
    fontSize: 13,
    background: C.bgCard,
    color: '#463F34',
    minWidth: 130,
    cursor: 'pointer',
    boxShadow: '0px 10px 18px rgba(18,18,18,0.03), inset 0px 1px 0px rgba(255,255,255,0.9)',
  },

  // Add Vendor Button (D5)
  addBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '10px 18px',
    background: '#C6A75A',
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.26)',
    cursor: 'pointer',
    boxShadow: '0px 16px 30px rgba(18,18,18,0.10), inset 0px 1px 0px rgba(255,255,255,0.28)',
    transition: 'all 0.15s ease',
  },
  addBtnText: {
    fontSize: 12,
    fontWeight: 700,
    color: '#FFFFFF',
    letterSpacing: '0.2px',
  },

  // Ledger Shell (Table Container)
  ledgerShell: {
    marginBottom: 20,
    borderRadius: 22,
    border: '1px solid rgba(198,167,90,0.30)',
    borderTop: '2px solid #C6A75A',
    background: 'linear-gradient(150deg, rgba(255,255,255,0.50), rgba(255,255,255,0.30))',
    backdropFilter: 'blur(28px) saturate(160%) brightness(1.06)',
    WebkitBackdropFilter: 'blur(28px) saturate(160%) brightness(1.06)',
    padding: 18,
    boxShadow: '0px 34px 70px rgba(18,18,18,0.10), 0px 12px 26px rgba(18,18,18,0.055), inset 0px 1px 0px rgba(255,255,255,0.96)',
  },
  ledgerHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    padding: '0 2px',
  },
  ledgerEyebrow: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.9px',
    color: '#4F4838',
  },
  ledgerMeta: {
    fontSize: 10,
    color: '#5C5446',
    fontWeight: 600,
  },
  // Ledger summary chips (ported from the approved sketch)
  ledgerStatRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  ledgerStat: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    lineHeight: 1.05,
    padding: '5px 12px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.55)',
    border: '1px solid rgba(198,167,90,0.20)',
  },
  ledgerStatValue: {
    fontSize: 14,
    fontWeight: 700,
    color: '#2A2622',
    fontVariantNumeric: 'tabular-nums',
  },
  ledgerStatLabel: {
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#5C5446',
    marginTop: 2,
  },

  // Table Header
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 14px',
    borderRadius: 14,
    background: C.bgTableDivider,
    border: `1px solid ${C.borderLight}`,
    marginBottom: 12,
    boxShadow: '0px 12px 20px rgba(18,18,18,0.03), inset 0px 1px 0px rgba(255,255,255,0.92)',
  },
  tableHeaderText: {
    fontSize: 9.5,
    letterSpacing: '0.85px',
    color: '#4F4838',
    fontWeight: 600,
    textTransform: 'uppercase',
  },

  // ═══ PART 2: Group Shell (E1-E2) ═══════════════════════════════════════════════
  groupShell: {
    borderRadius: 14,
    border: `1px solid ${C.borderMedium}`,
    background: C.bgTableDivider,
    padding: 11,
    marginTop: 8,
    boxShadow: '0px 24px 42px rgba(18,18,18,0.055), inset 0px 1px 0px rgba(255,255,255,0.92)',
  },
  groupShellFirst: {
    marginTop: 0,
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '9px 10px',
    borderBottom: `1px solid ${C.borderLight}`,
    marginBottom: 6,
    background: C.bgTableDivider,
    borderRadius: 8,
  },
  groupTitle: {
    fontSize: 10,
    letterSpacing: '1.1px',
    color: '#4F4838',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  groupRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  miniTrack: {
    width: 54,
    height: 4,
    background: 'rgba(20,20,28,0.08)',
    borderRadius: 999,
    overflow: 'hidden',
  },
  miniFill: {
    height: '100%',
    borderRadius: 999,
    transition: 'width 0.3s ease',
  },
  groupPct: {
    fontSize: 10,
    color: '#5C5446',
    fontWeight: 600,
  },
  groupAmt: {
    fontSize: 10,
    color: '#C6A75A',
    fontWeight: 600,
  },

  // Vendor Row
  vendorRow: {
    padding: '16px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.60)',
    border: '1px solid rgba(198,167,90,0.16)',
    marginBottom: 8,
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    boxShadow: '0px 20px 38px rgba(18,18,18,0.06), 0px 4px 10px rgba(18,18,18,0.025), inset 0px 1px 0px rgba(255,255,255,0.94)',
  },
  vendorRowHover: {
    background: C.statusWarningTint,
    borderColor: 'rgba(198,167,90,0.28)',
  },
  vendorRowExpanded: {
    background: C.statusWarningTint,
    borderColor: 'rgba(198,167,90,0.28)',
    boxShadow: '0px 24px 44px rgba(18,18,18,0.07), 0px 6px 12px rgba(18,18,18,0.03), inset 0px 1px 0px rgba(255,255,255,0.95)',
  },
  vendorRowTop: {
    display: 'flex',
    alignItems: 'center',
  },

  // Cell styling
  valueCell: {
    minHeight: 40,
    padding: '0 9px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderLeft: '1px solid rgba(198,167,90,0.18)',
  },
  vendorName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1F1C17',
    letterSpacing: '-0.2px',
  },
  vendorSub: {
    fontSize: 10,
    color: '#5C5446',
    marginTop: 2,
    letterSpacing: '0.05px',
  },
  valueText: {
    fontSize: 12,
    color: '#6F685C',
    fontWeight: 600,
    textAlign: 'center',
  },
  valueTextGreen: { color: '#4A8C5A' },
  valueTextOrange: { color: '#C8783A' },
  valueTextRed: { color: '#dc2626' },

  // ═══ PART 2: Mini Progress Bar (E4) ════════════════════════════════════════════
  miniProgressWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  miniProgressTrack: {
    width: '100%',
    height: 6,
    background: 'rgba(20,20,28,0.08)',
    borderRadius: 999,
    overflow: 'hidden',
  },
  miniProgressFill: {
    height: '100%',
    borderRadius: 999,
    transition: 'width 0.3s ease',
  },
  miniProgressLabel: {
    fontSize: 10,
    color: '#5C5446',
    fontWeight: 600,
  },

  // Status Pill (E7)
  statusPill: {
    minHeight: 30,
    padding: '0 10px',
    borderRadius: 999,
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 9.5,
    letterSpacing: '0.55px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },

  // ═══ PART 2: Row Action Menu (E8) ══════════════════════════════════════════════
  actionMenuWrap: {
    position: 'relative',
  },
  overflowBtn: {
    padding: '6px 8px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 8,
    transition: 'background 0.15s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    background: C.bgCard,
    border: '1px solid rgba(198,167,90,0.22)',
    borderRadius: 12,
    boxShadow: '0px 20px 40px rgba(18,18,18,0.15)',
    zIndex: 200,
    minWidth: 160,
    overflow: 'hidden',
  },
  actionMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    fontSize: 12,
    color: '#463F34',
    cursor: 'pointer',
    transition: 'background 0.15s',
    border: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
  },
  actionMenuItemDanger: {
    color: '#dc2626',
  },

  // ═══ PART 2: Expandable Row Details (F1-F5) ════════════════════════════════════
  expandedSection: {
    paddingTop: 16,
    borderTop: '1px solid rgba(198,167,90,0.12)',
    marginTop: 12,
  },
  expandedGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },

  // Milestone Track (F1)
  milestoneWrap: {
    paddingTop: 12,
    paddingBottom: 8,
  },
  milestoneLabel: {
    fontSize: 10,
    letterSpacing: '0.85px',
    color: '#4F4838',
    marginBottom: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  milestoneRail: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  milestoneStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 70,
  },
  milestoneDot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    border: '2px solid rgba(20,20,28,0.14)',
    background: C.bgCard,
  },
  milestoneDotDone: {
    background: '#4A8C5A',
    borderColor: '#4A8C5A',
  },
  milestoneDotNext: {
    borderColor: '#C6A75A',
    background: C.bgCard,
  },
  milestoneLine: {
    flex: 1,
    height: 2,
    background: 'rgba(20,20,28,0.08)',
    marginTop: 6,
    marginLeft: -4,
    marginRight: -4,
  },
  milestoneLineDone: {
    background: '#4A8C5A',
  },
  milestoneText: {
    fontSize: 9,
    color: '#5C5446',
    marginTop: 6,
    textAlign: 'center',
    lineHeight: '12px',
  },
  milestoneAmt: {
    fontSize: 10,
    fontWeight: 600,
    color: '#6F685C',
    marginTop: 2,
  },
  milestoneAmtGreen: { color: '#4A8C5A' },
  milestoneAmtGold: { color: '#C6A75A' },

  // Contact Grid (F2)
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 10,
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    background: C.bgCard,
    borderRadius: 10,
    border: '1px solid rgba(198,167,90,0.12)',
  },
  contactLabel: {
    fontSize: 9,
    color: '#5C5446',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  contactValue: {
    fontSize: 12,
    color: '#1F1C17',
    fontWeight: 500,
  },

  // Action Buttons (F3-F4)
  expandedActions: {
    display: 'flex',
    gap: 10,
    marginTop: 16,
  },
  settleBtn: {
    flex: 1,
    minHeight: 38,
    borderRadius: 12,
    padding: '10px 16px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.1px',
    transition: 'all 0.15s ease',
  },
  settleBtnPrimary: {
    background: '#C6A75A',
    borderColor: 'rgba(255,255,255,0.26)',
    color: '#FFFFFF',
  },
  settleBtnSecondary: {
    background: C.bgTableDivider,
    borderColor: 'rgba(198,167,90,0.22)',
    color: '#6F685C',
  },
  paidBadge: {
    minHeight: 34,
    borderRadius: 999,
    padding: '8px 14px',
    background: '#F0FAF5',
    border: '1px solid rgba(47,122,87,0.22)',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  paidBadgeText: {
    fontSize: 11,
    fontWeight: 700,
    color: '#2F7A57',
    letterSpacing: '0.15px',
    textTransform: 'uppercase',
  },

  // Totals Row (K1-K3)
  totalsRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: 14,
    background: C.bgTableDivider,
    border: `1px solid ${C.borderMedium}`,
    marginTop: 12,
    boxShadow: '0px 14px 28px rgba(18,18,18,0.04), inset 0px 1px 0px rgba(255,255,255,0.92)',
  },
  totalsLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#4F4838',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    width: 260,
    paddingLeft: 16,
  },
  totalsValue: {
    fontSize: 13,
    fontWeight: 700,
    textAlign: 'center',
  },

  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    color: '#5C5446',
  },
  emptyIcon: {
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 14,
    fontWeight: 600,
    color: '#4F4838',
  },
  emptySubtext: {
    fontSize: 12,
    color: '#A89C82',
    marginTop: 4,
  },

  // ═══ PART 4: Intelligence Cards (B1-B3) ══════════════════════════════════════
  intelligenceRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    marginBottom: 18,
  },
  intelligenceCard: {
    padding: '18px 20px',
    borderRadius: 14,
    border: '1px solid rgba(198,167,90,0.22)',
    background: 'linear-gradient(150deg, rgba(255,255,255,0.50), rgba(255,255,255,0.30))',
    backdropFilter: 'blur(28px) saturate(160%) brightness(1.06)',
    WebkitBackdropFilter: 'blur(28px) saturate(160%) brightness(1.06)',
    boxShadow: '0px 20px 40px rgba(18,18,18,0.06), inset 0px 1px 0px rgba(255,255,255,0.94)',
    transition: 'all 0.2s ease',
  },
  intelligenceCardHover: {
    // No movement on hover (house rule) — gold ring + deeper shadow only.
    borderColor: 'rgba(198,167,90,0.45)',
    boxShadow: '0px 26px 52px rgba(18,18,18,0.10), 0px 0px 0px 1px rgba(198,167,90,0.18), inset 0px 1px 0px rgba(255,255,255,0.94)',
  },
  intelligenceCardDrop: {
    borderStyle: 'dashed',
    borderColor: '#C6A75A',
    background: C.statusWarningTint,
  },
  intelligenceHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  intelligenceIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  intelligenceIconOcr: { background: 'rgba(198,167,90,0.15)' },
  intelligenceIconTransparency: { background: 'rgba(74,140,90,0.15)' },
  intelligenceIconUpcoming: { background: 'rgba(59,130,246,0.15)' },
  // WOW Contract Scanner (the hero intelligence card) — white, gold top-accent + glowing M orb.
  scannerCard: {
    position: 'relative',
    overflow: 'hidden',
    borderColor: 'rgba(198,167,90,0.50)',
    boxShadow: '0px 26px 54px rgba(18,18,18,0.10), 0px 0px 0px 1px rgba(198,167,90,0.18), inset 0px 1px 0px rgba(255,255,255,0.96)',
  },
  scannerTopAccent: {
    position: 'absolute', left: 0, right: 0, top: 0, height: 3, zIndex: 2,
    background: 'linear-gradient(90deg, transparent, #E6C579, #C6A75A, transparent)',
  },
  scannerOrb: {
    width: 36, height: 36, borderRadius: '50%',
    background: 'radial-gradient(circle at 35% 30%, #E6C579, #C6A75A)',
    color: '#221b08', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 18, flexShrink: 0,
    boxShadow: '0 0 0 5px rgba(198,167,90,0.16), 0 6px 16px rgba(198,167,90,0.40)',
  },
  scannerActionGold: {
    marginTop: 12, padding: '9px 15px', borderRadius: 11, border: 'none', cursor: 'pointer',
    fontSize: 11.5, fontWeight: 700, color: '#221b08',
    background: 'linear-gradient(180deg, #E6C579, #C6A75A)',
    boxShadow: '0 6px 16px rgba(198,167,90,0.34)',
    display: 'inline-flex', alignItems: 'center', gap: 7,
  },
  intelligenceTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#1F1C17',
    letterSpacing: '-0.1px',
  },
  intelligenceSub: {
    fontSize: 11,
    color: '#5C5446',
    marginTop: 2,
  },
  intelligenceBody: {
    fontSize: 12,
    color: '#6F685C',
    lineHeight: '18px',
  },
  intelligenceHighlight: {
    fontWeight: 700,
    color: '#C6A75A',
  },
  intelligenceHighlightGreen: {
    fontWeight: 700,
    color: '#4A8C5A',
  },
  intelligenceHighlightRed: {
    fontWeight: 700,
    color: '#dc2626',
  },
  intelligenceAction: {
    marginTop: 12,
    padding: '8px 14px',
    borderRadius: 10,
    border: '1px solid rgba(198,167,90,0.22)',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 700,
    color: '#876B2C',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s',
  },
  intelligenceList: {
    marginTop: 10,
  },
  intelligenceListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 0',
    fontSize: 12,
    color: '#463F34',
  },
  // Transparency Watch — gold ring (ported from the approved sketch)
  twWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    padding: '14px 2px 6px',
  },
  twRingWrap: {
    position: 'relative',
    display: 'grid',
    placeItems: 'center',
    flex: 'none',
  },
  twRingLabel: {
    position: 'absolute',
    fontSize: 26,
    fontWeight: 700,
    fontFamily: "'Playfair Display', serif",
    color: '#2A2622',
    fontVariantNumeric: 'tabular-nums',
  },
  twStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: 11,
    flex: 1,
  },

  // ═══ PART 4: Alert Bar (C1-C3) ════════════════════════════════════════════════
  alertBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 20px',
    borderRadius: 14,
    marginBottom: 18,
    animation: 'fadeIn 0.3s ease',
  },
  alertBarUrgent: {
    background: 'rgba(220,53,69,0.08)',
    border: '1px solid rgba(220,53,69,0.22)',
  },
  alertBarWarning: {
    background: 'rgba(245,158,11,0.08)',
    border: '1px solid rgba(245,158,11,0.22)',
  },
  alertBarIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  alertBarIconUrgent: {
    background: 'rgba(220,53,69,0.15)',
  },
  alertBarIconWarning: {
    background: 'rgba(245,158,11,0.15)',
  },
  alertBarContent: {
    flex: 1,
  },
  alertBarTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#1F1C17',
    marginBottom: 2,
  },
  alertBarText: {
    fontSize: 12,
    color: '#6F685C',
    lineHeight: '16px',
  },
  alertBarActions: {
    display: 'flex',
    gap: 8,
    flexShrink: 0,
  },
  alertBarBtn: {
    padding: '8px 14px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  alertBarBtnPrimary: {
    background: '#dc2626',
    border: 'none',
    color: '#FFFFFF',
  },
  alertBarBtnSecondary: {
    background: 'transparent',
    border: '1px solid rgba(107,114,128,0.22)',
    color: '#6B7280',
  },

  // ═══ PART 4: Undo Snackbar (L1-L3) ════════════════════════════════════════════
  undoSnackbar: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 20px',
    borderRadius: 14,
    background: '#1F1C17',
    color: '#FFFFFF',
    boxShadow: '0px 20px 40px rgba(0,0,0,0.3)',
    zIndex: 1200,
    animation: 'slideUp 0.3s ease',
  },
  undoText: {
    fontSize: 13,
    fontWeight: 500,
  },
  undoBtn: {
    padding: '6px 14px',
    borderRadius: 8,
    background: '#C6A75A',
    border: 'none',
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  undoClose: {
    padding: 4,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    marginLeft: 4,
  },
};

// Inject Part 4 keyframes
if (typeof document !== 'undefined') {
  const part4StyleId = 'vendor-part4-keyframes';
  if (!document.getElementById(part4StyleId)) {
    const style = document.createElement('style');
    style.id = part4StyleId;
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translate(-50%, 20px); }
        to { opacity: 1; transform: translate(-50%, 0); }
      }
    `;
    document.head.appendChild(style);
  }
}

// ─── Sub-Components ────────────────────────────────────────────────────────────

// E3: Blinking Dot Component
const BlinkingDot = ({ color, blink, size = 8 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: size / 2,
      background: color,
      animation: blink ? 'vendorDotBlink 1.2s ease-in-out infinite' : 'none',
    }}
  />
);

// E4: Mini Progress Bar Component
const MiniProgressBar = ({ paid, total, showLabel = true }) => {
  const pct = total > 0 ? Math.round((paid / total) * 100) : 0;
  const fillColor = pct >= 100 ? '#4A8C5A' : pct > 50 ? '#C6A75A' : '#EAB354';
  
  return (
    <div style={styles.miniProgressWrap}>
      <div style={styles.miniProgressTrack}>
        <div
          style={{
            ...styles.miniProgressFill,
            width: `${Math.min(pct, 100)}%`,
            background: fillColor,
          }}
        />
      </div>
      {showLabel && (
        <span style={styles.miniProgressLabel}>{pct}% paid</span>
      )}
    </div>
  );
};

// F1: Payment Milestone Track
const PaymentMilestoneTrack = ({ vendor }) => {
  const contract = getVendorContract(vendor);
  const paid = getVendorPaid(vendor);
  const deposit = vendor.deposit_amount || Math.round(contract * 0.5);
  const depositPaid = paid >= deposit;
  const fullyPaid = paid >= contract;
  
  const steps = [
    { label: 'Deposit', amount: deposit, done: depositPaid },
    { label: 'Balance', amount: contract - deposit, done: fullyPaid },
  ];
  
  return (
    <div style={styles.milestoneWrap}>
      <div style={styles.milestoneLabel}>Payment Milestones</div>
      <div style={styles.milestoneRail}>
        {steps.map((step, i) => (
          <React.Fragment key={step.label}>
            <div style={styles.milestoneStep}>
              <div
                style={{
                  ...styles.milestoneDot,
                  ...(step.done ? styles.milestoneDotDone : {}),
                  ...(!step.done && i === 0 ? {} : {}),
                  ...(!step.done && steps[i - 1]?.done ? styles.milestoneDotNext : {}),
                }}
              >
                {step.done && (
                  <CheckCircle size={10} color="#FFFFFF" style={{ margin: 'auto', display: 'block' }} />
                )}
              </div>
              <span style={styles.milestoneText}>{step.label}</span>
              <span
                style={{
                  ...styles.milestoneAmt,
                  ...(step.done ? styles.milestoneAmtGreen : styles.milestoneAmtGold),
                }}
              >
                {formatMoney(step.amount)}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  ...styles.milestoneLine,
                  ...(steps[i + 1]?.done || step.done ? styles.milestoneLineDone : {}),
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// E8: Row Action Menu
const RowActionMenu = ({ vendor, onEdit, onDelete, onViewContract, onLogPayment, onReviewPayment, isOpen, onToggle }) => {
  return (
    <div style={styles.actionMenuWrap}>
      <button
        style={styles.overflowBtn}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(198,167,90,0.15)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <MoreHorizontal size={18} color="#5C5446" />
      </button>
      {isOpen && (
        <div style={styles.actionMenu}>
          <button
            style={styles.actionMenuItem}
            onClick={(e) => { e.stopPropagation(); onEdit(); onToggle(); }}
            onMouseEnter={(e) => e.currentTarget.style.background = C.statusWarningTint}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Edit2 size={14} /> Edit Details
          </button>
          <button
            style={styles.actionMenuItem}
            onClick={(e) => { e.stopPropagation(); onLogPayment(); onToggle(); }}
            onMouseEnter={(e) => e.currentTarget.style.background = C.statusWarningTint}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <CreditCard size={14} /> Log Payment
          </button>
          <button
            style={styles.actionMenuItem}
            onClick={(e) => { e.stopPropagation(); onReviewPayment(); onToggle(); }}
            onMouseEnter={(e) => e.currentTarget.style.background = C.statusWarningTint}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Clock size={14} /> Review Payments
          </button>
          <button
            style={styles.actionMenuItem}
            onClick={(e) => { e.stopPropagation(); onViewContract(); onToggle(); }}
            onMouseEnter={(e) => e.currentTarget.style.background = C.statusWarningTint}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <FileText size={14} /> View Contract
          </button>
          {vendor.website && (
            <button
              style={styles.actionMenuItem}
              onClick={(e) => { e.stopPropagation(); window.open(vendor.website, '_blank'); onToggle(); }}
              onMouseEnter={(e) => e.currentTarget.style.background = C.statusWarningTint}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <ExternalLink size={14} /> Visit Website
            </button>
          )}
          <button
            style={{ ...styles.actionMenuItem, ...styles.actionMenuItemDanger }}
            onClick={(e) => { e.stopPropagation(); onDelete(); onToggle(); }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(220,53,69,0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Trash2 size={14} /> Delete Vendor
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const VendorsTab = ({ deepLinkEntity, onVendorChange, focusEntity = null, onNavigateToReadiness = null }) => {
  // State
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  // Phase 2 — bidirectional orchestration awareness. Keyed by vendor id;
  // each value is { count, op_ids, titles }. Empty when no awareness yet.
  const [orchestrationAwareness, setOrchestrationAwareness] = useState({});
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPayment, setFilterPayment] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [missingContractOnly, setMissingContractOnly] = useState(false);
  const [sortKey, setSortKey] = useState('due_date');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [stats, setStats] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  
  // Part 3: Drawer state
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerVendor, setDrawerVendor] = useState(null);

  // Part 4: Payment Modals
  const [showLogPayment, setShowLogPayment] = useState(false);
  const [showReviewPayment, setShowReviewPayment] = useState(false);
  const [paymentVendor, setPaymentVendor] = useState(null);

  // Part 4: Alert Bar (C1-C3)
  const [alertDismissed, setAlertDismissed] = useState(false);

  // Part 4: Auto-Save & Undo (L1-L3)
  const [pendingChanges, setPendingChanges] = useState(null);
  const [showUndoSnackbar, setShowUndoSnackbar] = useState(false);
  const [undoData, setUndoData] = useState(null);
  const saveTimeoutRef = useRef(null);
  const contractFileInputRef = useRef(null);
  // #92: accumulate the *latest* draft vendor + the union of edited field-keys
  // across one debounce window. Refs (not state) so successive edits inside the
  // 5s buffer always read the freshest accumulation without stale closures.
  const pendingVendorRef = useRef(null);
  const pendingFieldsRef = useRef(new Set());
  // #92: genuine pre-edit snapshot for the Undo snackbar — captured ONCE at the
  // first edit of a debounce window, before any optimistic mutation, so Undo
  // restores the real prior values (not the post-edit accumulation).
  const preEditSnapshotRef = useRef(null);
  // #92: persisted-confirmation signal handed to the drawer. Only set AFTER the
  // PATCH resolves, so "Saved" reflects what truly landed (no false confirmation).
  const [drawerSavedSignal, setDrawerSavedSignal] = useState(null);

  // Part 4: OCR State (B1)
  const [ocrDragging, setOcrDragging] = useState(false);
  const [contractFile, setContractFile] = useState(null);   // Milo Contract Intelligence flow

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterCategory !== 'All') params.append('role_category', filterCategory);
      if (filterStatus !== 'All') params.append('vendor_status', filterStatus);
      if (filterPayment !== 'All') params.append('payment_status', filterPayment);
      
      const res = await axios.get(`${API_URL}/api/vendors?${params.toString()}`);
      setVendors(res.data);
    } catch (err) {
      console.error('Error fetching vendors:', err);
    } finally {
      setLoading(false);
    }
  }, [search, filterCategory, filterStatus, filterPayment]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/vendors/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching vendor stats:', err);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchStats();
  }, [fetchVendors]);

  // Phase 2 — orchestration awareness: load + on focusEntity arrivals so the
  // indicator reflects the latest live signal count.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getModuleAwareness('vendors');
        if (!cancelled && data && typeof data === 'object') {
          setOrchestrationAwareness(data);
        }
      } catch (_err) { /* awareness is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [vendors.length, focusEntity]);

  // Phase 2 — scroll into view when a back-link from Event Readiness arrives
  // with a specific vendor entity_id.
  useEffect(() => {
    if (!focusEntity?.entity_id) return;
    const el = document.querySelector(`[data-testid="vendor-row-${focusEntity.entity_id}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusEntity]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
      setSortDropdownOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // ─── Computed Values ───────────────────────────────────────────────────────

  // Filter vendors by category chip (D6) and, optionally, missing-contract toggle
  const filteredVendors = useMemo(() => {
    let list = vendors;
    if (categoryFilter !== 'all') {
      list = list.filter(v => {
        const group = getVendorExperienceGroup(v.role_category || v.category);
        return group === categoryFilter;
      });
    }
    if (missingContractOnly) {
      list = list.filter(v => !(v.attachments || []).some(a => a?.type === 'contract'));
    }
    return list;
  }, [vendors, categoryFilter, missingContractOnly]);

  // Sort vendors (D7)
  const sortedVendors = useMemo(() => {
    return sortVendors(filteredVendors, sortKey);
  }, [filteredVendors, sortKey]);

  // E1: Group vendors by experience
  const groupedVendors = useMemo(() => {
    return groupVendorsByExperience(sortedVendors);
  }, [sortedVendors]);

  // Calculate totals (K1-K3)
  const totals = useMemo(() => {
    return {
      contract: vendors.reduce((sum, v) => sum + getVendorContract(v), 0),
      paid: vendors.reduce((sum, v) => sum + getVendorPaid(v), 0),
      balance: vendors.reduce((sum, v) => sum + getVendorRemaining(v), 0),
    };
  }, [vendors]);

  // Part 4: Intelligence Cards computed values (B1-B3)
  const missingContractVendors = useMemo(() => getMissingContractVendors(vendors), [vendors]);
  const upcomingPaymentVendors = useMemo(() => getUpcomingPaymentVendors(vendors, 30), [vendors]);
  const urgentVendor = useMemo(() => getUrgentVendor(vendors), [vendors]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleAddVendor = () => {
    setEditingVendor(null);
    setShowModal(true);
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setShowModal(true);
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await axios.delete(`${API_URL}/api/vendors/${vendorId}`);
      fetchVendors();
      fetchStats();
      if (onVendorChange) onVendorChange();
    } catch (err) {
      alert('Error deleting vendor');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingVendor(null);
  };

  const handleVendorSaved = () => {
    handleModalClose();
    fetchVendors();
    fetchStats();
    if (onVendorChange) onVendorChange();
  };

  // #92: flush any buffered (un-PATCHed) drawer edits immediately. Called before
  // we switch the drawer to a different vendor or close it, so (a) edits aren't
  // lost if the host closes inside the 5s window, and (b) one vendor's pending
  // batch can never merge onto another vendor opened next. Fire-and-forget.
  const flushPendingSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    const payload = pendingVendorRef.current;
    pendingVendorRef.current = null;
    pendingFieldsRef.current = new Set();
    preEditSnapshotRef.current = null;
    setPendingChanges(null);
    if (payload && payload.id) {
      axios.patch(`${API_URL}/api/vendors/${payload.id}`, payload)
        .then(() => { fetchVendors(); fetchStats(); if (onVendorChange) onVendorChange(); })
        .catch((err) => { console.error('Error flushing vendor save:', err); });
    }
  }, [fetchVendors, onVendorChange]);

  // #3: explicit "Save now" from the drawer's footer button. Unlike the
  // fire-and-forget flush above, this awaits the PATCH, flashes "Saved" only on
  // success (driven through the same persisted-only signal as the debounced
  // path), and surfaces a failure toast — so the host gets a real, immediate
  // confirmation instead of waiting out the 5s buffer (or trusting nothing).
  const handleDrawerSaveNow = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    const payload = pendingVendorRef.current;
    const persistedFields = Array.from(pendingFieldsRef.current);
    pendingVendorRef.current = null;
    pendingFieldsRef.current = new Set();
    preEditSnapshotRef.current = null;
    setPendingChanges(null);
    if (!payload || !payload.id) {
      // Nothing buffered (everything already persisted) — still confirm to the host.
      setDrawerSavedSignal({ fields: ['__all__'], at: Date.now() });
      return;
    }
    try {
      await axios.patch(`${API_URL}/api/vendors/${payload.id}`, payload);
      fetchVendors();
      fetchStats();
      if (onVendorChange) onVendorChange();
      setDrawerSavedSignal({ fields: persistedFields.length ? persistedFields : ['__all__'], at: Date.now() });
    } catch (err) {
      console.error('Error saving vendor:', err);
      toast.error('Could not save your changes. Please try again.');
    }
  }, [fetchVendors, onVendorChange]);

  // E9: Row click opens detail drawer (Part 3)
  const handleRowClick = (vendor) => {
    // If a different vendor had buffered edits, persist them before switching.
    if (pendingVendorRef.current && pendingVendorRef.current.id !== (vendor?.id)) {
      flushPendingSave();
    }
    setDrawerVendor(vendor);
    setShowDrawer(true);
  };

  // Toggle expanded row (for inline details)
  const handleRowExpand = (vendor, e) => {
    e.stopPropagation();
    setExpandedRowId(expandedRowId === vendor.id ? null : vendor.id);
  };

  const handleViewContract = (vendor) => {
    // Find the most recent "contract" attachment, or fall back to the first doc attachment.
    const atts = Array.isArray(vendor?.attachments) ? vendor.attachments : [];
    const contract = atts.find(a => a.type === 'contract') || atts[0];
    if (!contract || !contract.url) {
      // No contract uploaded yet — open the drawer so the host can upload one.
      // #92: flush a different vendor's buffered edits before switching context.
      if (pendingVendorRef.current && pendingVendorRef.current.id !== (vendor?.id)) {
        flushPendingSave();
      }
      setDrawerVendor(vendor);
      setShowDrawer(true);
      toast.message?.(`No contract on file for ${vendor.company_name}. Upload one in the drawer.`) || toast.success?.(`Open ${vendor.company_name} to upload a contract.`);
      return;
    }
    const url = contract.url.startsWith('http') ? contract.url : `${API_URL}${contract.url}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Part 3: Drawer handlers
  const handleDrawerClose = () => {
    // #92: persist any buffered edits before the drawer closes so a close inside
    // the 5s debounce window never silently drops the host's last changes.
    flushPendingSave();
    setShowDrawer(false);
    setDrawerVendor(null);
  };

  // Part 4: Auto-save with buffer (L1) — #92 rapid-edit-drop fix.
  //
  // BUG (before): each drawer edit called this with a full `{...vendor, field}`
  // object spread from the (stale) vendor prop, and we blindly stored it as the
  // pending payload. During the 5s debounce the prop never changed, so a 2nd
  // edit's object did NOT contain the 1st edit — yet every edit flashed "Saved".
  // The debounced PATCH wrote only the LAST edit; earlier ones were silently lost.
  //
  // FIX: accumulate. We merge ONLY the changed key(s) of each edit onto the
  // latest accumulated draft (pendingVendorRef ?? drawerVendor), optimistically
  // push that draft back into `drawerVendor` so the drawer always re-spreads from
  // fresh data, and union the changed field-keys. The single coalesced PATCH then
  // carries EVERY edited field, and we flash "Saved" (via drawerSavedSignal) only
  // for the fields that actually persisted.
  const handleDrawerSave = async (incomingVendor, changedFields = null) => {
    if (!incomingVendor) return;

    // Base = freshest accumulation this window, else the current drawer vendor.
    const base = pendingVendorRef.current || drawerVendor || incomingVendor;

    // Capture the true pre-edit snapshot once, at the first edit of this window
    // (before the optimistic mutation below), so Undo restores real prior values.
    if (!pendingVendorRef.current) {
      preEditSnapshotRef.current = { ...(drawerVendor || incomingVendor) };
    }

    // Determine which keys this edit actually changed. The drawer passes them
    // explicitly; if omitted (defensive), diff the incoming object against base.
    let keys = Array.isArray(changedFields) ? changedFields.filter(Boolean) : null;
    if (!keys || keys.length === 0) {
      keys = Object.keys(incomingVendor).filter((k) => incomingVendor[k] !== base[k]);
    }

    // Surgically overlay ONLY the changed keys so a full-object spread carrying a
    // stale value can never clobber a newer accumulated field.
    const merged = { ...base };
    keys.forEach((k) => { merged[k] = incomingVendor[k]; });
    // `balance_due_date` edits also carry a synced `due_date` from the drawer.
    if (keys.includes('balance_due_date') && 'due_date' in incomingVendor) {
      merged.due_date = incomingVendor.due_date;
    }

    // Persist the accumulation (ref for closures, state to keep existing wiring).
    pendingVendorRef.current = merged;
    keys.forEach((k) => pendingFieldsRef.current.add(k));
    setPendingChanges(merged);
    // Optimistically refresh the prop the drawer re-spreads from — kills the stale merge.
    setDrawerVendor(merged);

    // Restart the debounce window.
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // L1: 5-second save buffer (coalesces a burst of edits into one write).
    saveTimeoutRef.current = setTimeout(async () => {
      const payload = pendingVendorRef.current;
      const persistedFields = Array.from(pendingFieldsRef.current);
      // True pre-edit state for Undo (captured at window start, pre-optimistic).
      const previousData = preEditSnapshotRef.current || { ...drawerVendor };
      // Reset the accumulators up front so edits made *during* the in-flight
      // request open a fresh window rather than re-sending this batch.
      pendingVendorRef.current = null;
      pendingFieldsRef.current = new Set();
      preEditSnapshotRef.current = null;
      try {
        await axios.patch(`${API_URL}/api/vendors/${payload.id}`, payload);
        fetchVendors();
        fetchStats();
        if (onVendorChange) onVendorChange();
        setPendingChanges(null);

        // #92: confirm "Saved" ONLY for fields that actually persisted.
        if (persistedFields.length > 0) {
          setDrawerSavedSignal({ fields: persistedFields, at: Date.now() });
        }

        // L2: Show undo snackbar
        setUndoData({ vendor: previousData, action: 'update' });
        setShowUndoSnackbar(true);
        setTimeout(() => setShowUndoSnackbar(false), 5000);
      } catch (err) {
        console.error('Error saving vendor:', err);
        setPendingChanges(null);
        // No "Saved" flash on failure — surface the miss instead of confirming it.
        toast.error('Could not save your change. Please try again.');
      }
    }, 5000);
  };

  // L2: Undo handler
  const handleUndo = async () => {
    if (!undoData) return;
    try {
      await axios.patch(`${API_URL}/api/vendors/${undoData.vendor.id}`, undoData.vendor);
      fetchVendors();
      fetchStats();
      if (onVendorChange) onVendorChange();
      setShowUndoSnackbar(false);
      setUndoData(null);
    } catch (err) {
      console.error('Error undoing:', err);
    }
  };

  // Part 4: Payment modal handlers
  const handleLogPayment = (vendor) => {
    flushPendingSave(); // #92: don't lose an in-progress field edit on the jump to payments
    setPaymentVendor(vendor);
    setShowLogPayment(true);
    setShowDrawer(false);
  };

  const handleReviewPayment = (vendor) => {
    flushPendingSave(); // #92: persist buffered edits before leaving the drawer
    setPaymentVendor(vendor);
    setShowReviewPayment(true);
    setShowDrawer(false);
  };

  const handlePaymentSubmit = async (paymentData) => {
    if (!paymentVendor) return;
    try {
      // Add payment to vendor's payments array
      const updatedPayments = [...(paymentVendor.payments || []), paymentData];
      const newPaidTotal = updatedPayments.reduce((sum, p) => sum + p.amount, 0);
      
      await axios.patch(`${API_URL}/api/vendors/${paymentVendor.id}`, {
        payments: updatedPayments,
        paid_to_date: newPaidTotal,
      });
      
      fetchVendors();
      fetchStats();
      setShowLogPayment(false);
      setPaymentVendor(null);
    } catch (err) {
      console.error('Error logging payment:', err);
    }
  };

  const handleReviewToLog = (vendor, amount) => {
    setShowReviewPayment(false);
    setPaymentVendor(vendor);
    setShowLogPayment(true);
  };

  // C3: Dismiss alert handler
  const handleDismissAlert = () => {
    setAlertDismissed(true);
  };

  // B1: OCR drag handlers
  const handleOcrDragOver = (e) => {
    e.preventDefault();
    setOcrDragging(true);
  };

  const handleOcrDragLeave = () => {
    setOcrDragging(false);
  };

  const handleOcrDrop = async (e) => {
    e.preventDefault();
    setOcrDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      handleContractFileSelected(file);
    }
  };

  const handleContractFileSelected = async (file) => {
    if (!file) return;
    // Validate file type (PDF or image — what Milo can read natively).
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    const okExt = /\.(pdf|png|jpe?g|webp)$/i.test(file.name || '');
    if (!validTypes.includes(file.type) && !okExt) {
      toast.error('Please select a PDF or image file');
      return;
    }
    // Open the governed Contract Intelligence flow: analyze → review → diff → apply → undo.
    setContractFile(file);
  };

  const handleContractUploadClick = () => {
    contractFileInputRef.current?.click();
  };

  const handleContractFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleContractFileSelected(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // ─── Render Vendor Row ─────────────────────────────────────────────────────

  const renderVendorRow = (vendor, isFirst = false) => {
    const contract = getVendorContract(vendor);
    const paid = getVendorPaid(vendor);
    const remaining = getVendorRemaining(vendor);
    const pct = contract > 0 ? Math.round((paid / contract) * 100) : 0;
    const isFullyPaid = remaining === 0 && contract > 0;
    const lastPayment = getLastPayment(vendor);
    const dueDate = vendor.due_date || vendor.balance_due_date || vendor.next_due_at;
    const dotSignal = getDotSignal(vendor);
    const daysRemaining = daysUntil(dueDate);
    const isExpanded = expandedRowId === vendor.id;
    const isHovered = hoveredRowId === vendor.id;
    
    // E7: Dynamic status determination
    let statusLabel = vendor.vendor_status || 'Pending';
    let statusColor = statusColors[statusLabel]?.text || '#566173';
    let statusBg = statusColors[statusLabel]?.bg || 'rgba(107,114,128,0.15)';
    
    if (isFullyPaid) {
      statusLabel = 'PAID';
      statusColor = '#4A8C5A';
      statusBg = 'rgba(74,140,90,0.15)';
    } else if (dotSignal.color === COLORS.error && dotSignal.blink) {
      statusLabel = 'OVERDUE';
      statusColor = '#dc2626';
      statusBg = 'rgba(220,38,38,0.15)';
    } else if (dotSignal.color === COLORS.error) {
      statusLabel = 'URGENT';
      statusColor = '#dc2626';
      statusBg = 'rgba(220,38,38,0.15)';
    }

    return (
      <div
        key={vendor.id}
        data-testid={`vendor-row-${vendor.id}`}
        onClick={() => handleRowClick(vendor)}
        onMouseEnter={() => setHoveredRowId(vendor.id)}
        onMouseLeave={() => setHoveredRowId(null)}
        style={{
          ...styles.vendorRow,
          ...(isHovered ? styles.vendorRowHover : {}),
          ...(isExpanded ? styles.vendorRowExpanded : {}),
        }}
      >
        <div style={styles.vendorRowTop}>
          {/* E10: Stoplight/Blinking Dot */}
          <div style={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BlinkingDot color={dotSignal.color} blink={dotSignal.blink} size={10} />
          </div>

          {/* Vendor Name & Category with E11, E12 */}
          <div style={{ width: 230, paddingRight: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={styles.vendorName}>{vendor.company_name}</span>
              {/* E11: Attachment Indicator — gold when any attachment exists, muted grey when none */}
              {(() => {
                const attCount = vendor.attachments?.length || 0;
                const hasContract = (vendor.attachments || []).some(a => a?.type === 'contract');
                const color = attCount > 0 ? COLORS.gold : '#C8BEA8';
                const label = attCount === 0
                  ? 'No attachments on file'
                  : hasContract
                    ? `${attCount} attachment${attCount === 1 ? '' : 's'} · contract on file`
                    : `${attCount} attachment${attCount === 1 ? '' : 's'} · no contract on file`;
                return (
                  <span
                    data-testid={`vendor-attachment-indicator-${vendor.id}`}
                    title={label}
                    aria-label={label}
                    style={{ display: 'inline-flex', alignItems: 'center', opacity: attCount > 0 ? 1 : 0.55 }}
                  >
                    <Paperclip size={12} color={color} />
                  </span>
                );
              })()}
              {/* E12: Needs Attention Flag */}
              {vendor.needs_attention && (
                <AlertCircle size={12} color="#dc2626" title="Needs attention" />
              )}
              {/* Phase 2 — orchestration awareness from Event Readiness */}
              {(() => {
                const aw = orchestrationAwareness[vendor.id];
                if (!aw || !aw.count) return null;
                return (
                  <OrchestrationIndicator
                    count={aw.count}
                    titles={aw.titles}
                    opIds={aw.op_ids}
                    onNavigate={onNavigateToReadiness}
                    ariaLabel={`${aw.count} open ${aw.count === 1 ? 'operation' : 'operations'} in Event Readiness for ${vendor.company_name}`}
                  />
                );
              })()}
            </div>
            <div style={styles.vendorSub}>
              {vendor.role_category}
              {vendor.contact_name && ` · ${vendor.contact_name}`}
            </div>
          </div>

          {/* E4: Mini Progress Bar */}
          <div style={{ ...styles.valueCell, width: 130, borderLeft: 'none' }}>
            <MiniProgressBar paid={paid} total={contract} />
          </div>

          {/* Total */}
          <div style={{ ...styles.valueCell, width: 78 }}>
            <span style={styles.valueText}>{formatMoney(contract)}</span>
          </div>

          {/* Due Date */}
          <div style={{ ...styles.valueCell, width: 84 }}>
            <span style={{
              ...styles.valueText,
              ...(daysRemaining !== null && daysRemaining <= 7 ? styles.valueTextRed : {}),
            }}>
              {dueDate ? formatShortDate(dueDate) : '—'}
            </span>
          </div>

          {/* E5: Last Payment Amount */}
          <div style={{ ...styles.valueCell, width: 64 }}>
            <span style={{
              ...styles.valueText,
              color: lastPayment ? '#4A8C5A' : '#9B9082',
            }}>
              {lastPayment ? formatMoney(lastPayment.amount) : '—'}
            </span>
          </div>

          {/* E6: Last Payment Date */}
          <div style={{ ...styles.valueCell, width: 68 }}>
            <span style={{ ...styles.valueText, color: '#4F4838' }}>
              {lastPayment ? formatShortDate(lastPayment.date) : '—'}
            </span>
          </div>

          {/* Balance */}
          <div style={{ ...styles.valueCell, width: 82 }}>
            <span style={{
              ...styles.valueText,
              ...(remaining > 0 ? styles.valueTextOrange : {}),
            }}>
              {isFullyPaid ? '—' : `-${formatMoney(remaining)}`}
            </span>
          </div>

          {/* E7: Status Pill */}
          <div style={{ ...styles.valueCell, width: 94 }}>
            <div style={{
              ...styles.statusPill,
              background: statusBg,
              borderColor: statusBg.replace('0.15', '0.3'),
            }}>
              <div style={{ ...styles.statusDot, background: statusColor }} />
              <span style={{ ...styles.statusText, color: statusColor }}>{statusLabel}</span>
            </div>
          </div>

          {/* E8: Actions Menu */}
          <div style={{ width: 50, display: 'flex', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
            {isFullyPaid && (
              <CheckCircle size={16} color="#4A8C5A" style={{ marginRight: 4, flexShrink: 0 }} />
            )}
            <RowActionMenu
              vendor={vendor}
              onEdit={() => handleEditVendor(vendor)}
              onDelete={() => handleDeleteVendor(vendor.id)}
              onViewContract={() => handleViewContract(vendor)}
              onLogPayment={() => handleLogPayment(vendor)}
              onReviewPayment={() => handleReviewPayment(vendor)}
              isOpen={openMenuId === vendor.id}
              onToggle={() => setOpenMenuId(openMenuId === vendor.id ? null : vendor.id)}
            />
          </div>
        </div>

        {/* F1-F5: Expandable Row Details */}
        {isExpanded && (
          <div style={styles.expandedSection}>
            <div style={styles.expandedGrid}>
              {/* F1: Payment Milestone Track */}
              <PaymentMilestoneTrack vendor={vendor} />

              {/* F2: Contact Details Grid */}
              <div>
                <div style={{ ...styles.milestoneLabel, marginBottom: 8 }}>Contact Details</div>
                <div style={styles.contactGrid}>
                  {vendor.contact_name && (
                    <div style={styles.contactItem}>
                      <User size={14} color="#5C5446" />
                      <div>
                        <div style={styles.contactLabel}>Contact</div>
                        <div style={styles.contactValue}>{vendor.contact_name}</div>
                      </div>
                    </div>
                  )}
                  {vendor.phone && (
                    <div style={styles.contactItem}>
                      <Phone size={14} color="#5C5446" />
                      <div>
                        <div style={styles.contactLabel}>Phone</div>
                        <div style={styles.contactValue}>{formatPhone(vendor.phone)}</div>
                      </div>
                    </div>
                  )}
                  {vendor.email && (
                    <div style={styles.contactItem}>
                      <Mail size={14} color="#5C5446" />
                      <div>
                        <div style={styles.contactLabel}>Email</div>
                        <div style={styles.contactValue}>{vendor.email}</div>
                      </div>
                    </div>
                  )}
                  {dueDate && (
                    <div style={styles.contactItem}>
                      <Calendar size={14} color="#5C5446" />
                      <div>
                        <div style={styles.contactLabel}>Due Date</div>
                        <div style={styles.contactValue}>{formatFullDate(dueDate)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* F3-F5: Action Buttons */}
            <div style={styles.expandedActions}>
              {isFullyPaid ? (
                <div style={styles.paidBadge}>
                  <CheckCircle size={16} color="#2F7A57" />
                  <span style={styles.paidBadgeText}>Fully Paid</span>
                </div>
              ) : (
                <>
                  {/* F3: Settle Balance Button */}
                  <button
                    style={{ ...styles.settleBtn, ...styles.settleBtnPrimary }}
                    onClick={(e) => { e.stopPropagation(); handleLogPayment(vendor); }}
                    data-testid={`settle-balance-${vendor.id}`}
                  >
                    <CreditCard size={14} />
                    Settle Balance ({formatMoney(remaining)})
                  </button>
                  {/* F4: View Contract Button */}
                  <button
                    style={{ ...styles.settleBtn, ...styles.settleBtnSecondary }}
                    onClick={(e) => { e.stopPropagation(); handleViewContract(vendor); }}
                  >
                    <FileText size={14} />
                    View Contract
                  </button>
                </>
              )}
              {/* F5: Edit Button */}
              <button
                style={{ ...styles.settleBtn, ...styles.settleBtnSecondary }}
                onClick={(e) => { e.stopPropagation(); handleEditVendor(vendor); }}
              >
                <Edit2 size={14} />
                Edit
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div data-testid="vendors-tab" style={styles.container}>
      {/* Authentic white-sphere background — four drifting pearl orbs behind the glass */}
      <div className="vsphere-bg" aria-hidden="true">
        <div className="vsphere-orb vsphere-orb1" />
        <div className="vsphere-orb vsphere-orb2" />
        <div className="vsphere-orb vsphere-orb3" />
        <div className="vsphere-orb vsphere-orb4" />
        <div className="vsphere-orb vsphere-orb5" />
        <div className="vsphere-orb vsphere-orb6" />
        <div className="vsphere-orb vsphere-orb7" />
        <div className="vsphere-orb vsphere-orb8" />
      </div>

      {/* ═══ PAGE HEADER (Dashboard standard) ════════════════════════════════════ */}
      <AdminPageHeader
        eyebrow="Vendors & Budget"
        title="Vendors"
        tagline="Bookings, contracts, and payments at a glance"
      />

      {/* ═══ A1-A6: KPI STRIP ═══════════════════════════════════════════════════ */}
      <div style={styles.kpiStrip} data-testid="vendor-kpi-strip">
        <div style={{ ...styles.kpiCell, ...styles.kpiCellBorder }}>
          <div style={styles.kpiLabel}>Total Vendors</div>
          <div style={styles.kpiValue}>{stats?.total_vendors || vendors.length}</div>
          <div style={styles.kpiSub}>partners engaged</div>
        </div>
        <div style={{ ...styles.kpiCell, ...styles.kpiCellBorder }}>
          <div style={styles.kpiLabel}>Contracts Signed</div>
          <div style={styles.kpiValue}>{stats?.by_status?.Hired || 0}</div>
          <div style={styles.kpiSub}>booked vendors</div>
        </div>
        <div style={{ ...styles.kpiCell, ...styles.kpiCellBorder, ...styles.kpiCellFinancial }}>
          <div style={styles.kpiLabel}>Total Contract</div>
          <div style={styles.kpiValueMoney}>{formatMoney(stats?.financials?.total_contract || totals.contract)}</div>
          <div style={styles.kpiSub}>committed spend</div>
        </div>
        <div style={{ ...styles.kpiCell, ...styles.kpiCellBorder, ...styles.kpiCellFinancial }}>
          <div style={styles.kpiLabel}>Total Paid</div>
          <div style={{ ...styles.kpiValueMoney, color: '#4A8C5A' }}>{formatMoney(stats?.financials?.total_paid || totals.paid)}</div>
          <div style={styles.kpiSub}>{Math.round(((stats?.financials?.total_paid || totals.paid) / ((stats?.financials?.total_contract || totals.contract) || 1)) * 100)}% of contract</div>
        </div>
        <div style={{ ...styles.kpiCell, ...styles.kpiCellBorder, ...styles.kpiCellFinancial }}>
          <div style={styles.kpiLabel}>Balance Due</div>
          <div style={{ ...styles.kpiValueMoney, color: '#C8783A' }}>{formatMoney(stats?.financials?.total_balance || totals.balance)}</div>
          <div style={styles.kpiSub}>{Math.round(((stats?.financials?.total_balance || totals.balance) / ((stats?.financials?.total_contract || totals.contract) || 1)) * 100)}% remaining</div>
        </div>
        <div style={styles.kpiCell}>
          <div style={styles.kpiLabel}>Vendor Meals</div>
          <div style={styles.kpiValue}>{stats?.vendor_meals || 0}</div>
          <div style={styles.kpiSub}>meals required</div>
        </div>
      </div>

      {/* ═══ B1-B3: INTELLIGENCE CARDS ════════════════════════════════════════════ */}
      <div style={styles.intelligenceRow} data-testid="vendor-intelligence-row">
        {/* B1: OCR Action Desk */}
        <div
          data-testid="ocr-action-desk"
          style={{
            ...styles.intelligenceCard,
            ...styles.scannerCard,
            ...(ocrDragging ? styles.intelligenceCardDrop : {}),
          }}
          onDragOver={handleOcrDragOver}
          onDragLeave={handleOcrDragLeave}
          onDrop={handleOcrDrop}
        >
          <div style={styles.scannerTopAccent} />
          <div style={styles.intelligenceHeader}>
            <div style={styles.scannerOrb}>M</div>
            <div>
              <div style={styles.intelligenceTitle}>Contract Scanner</div>
              <div style={styles.intelligenceSub}>Milo Contract Intelligence</div>
            </div>
          </div>
          <div style={styles.intelligenceBody}>
            {ocrDragging ? (
              <span style={styles.intelligenceHighlight}>Drop the contract here — Milo reads it.</span>
            ) : (
              <>
                Drop a contract — Milo extracts every term with <span style={styles.intelligenceHighlight}>confidence + the exact clause</span>, then proposes updates across Vendors, Finance, Calendar &amp; Readiness for your approval.
              </>
            )}
          </div>
          <button
            type="button"
            style={styles.scannerActionGold}
            onClick={handleContractUploadClick}
            data-testid="upload-contract-btn"
          >
            <Upload size={13} /> Upload Contract
          </button>
          {missingContractVendors.length > 0 && (
            <div style={{ fontSize: 10.5, color: '#5C5446', marginTop: 9 }}>
              {missingContractVendors.length} vendor{missingContractVendors.length !== 1 ? 's' : ''} still missing a contract.
            </div>
          )}
        </div>

        {/* B2: Transparency Watch */}
        <div style={styles.intelligenceCard} data-testid="transparency-watch">
          <div style={styles.intelligenceHeader}>
            <div style={{ ...styles.intelligenceIcon, ...styles.intelligenceIconTransparency }}>
              <Eye size={18} color="#4A8C5A" />
            </div>
            <div>
              <div style={styles.intelligenceTitle}>Transparency Watch</div>
              <div style={styles.intelligenceSub}>Payment Health</div>
            </div>
          </div>
          {(() => {
            const pctPaid = Math.round((totals.paid / (totals.contract || 1)) * 100);
            const RING_R = 33;
            const RING_C = 2 * Math.PI * RING_R;
            const ringOffset = RING_C * (1 - Math.min(Math.max(pctPaid, 0), 100) / 100);
            const fullyPaid = vendors.filter(v => getVendorRemaining(v) === 0 && getVendorContract(v) > 0).length;
            const openBalance = vendors.filter(v => getVendorRemaining(v) > 0).length;
            return (
              <div style={styles.twWrap}>
                <div style={styles.twRingWrap}>
                  <svg width="112" height="112" viewBox="0 0 80 80" style={{ display: 'block' }}>
                    <defs>
                      <linearGradient id="twGoldGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#E4CE92" />
                        <stop offset="55%" stopColor="#C6A75A" />
                        <stop offset="100%" stopColor="#9E7B31" />
                      </linearGradient>
                    </defs>
                    <circle cx="40" cy="40" r={RING_R} fill="none" stroke="rgba(20,20,28,0.08)" strokeWidth="7" />
                    <circle
                      cx="40" cy="40" r={RING_R} fill="none"
                      stroke="url(#twGoldGrad)" strokeWidth="7" strokeLinecap="round"
                      strokeDasharray={RING_C} strokeDashoffset={ringOffset}
                      transform="rotate(-90 40 40)"
                      style={{ transition: 'stroke-dashoffset 0.9s cubic-bezier(0.32,0.72,0,1)' }}
                    />
                  </svg>
                  <b style={styles.twRingLabel}>{pctPaid}%</b>
                </div>
                <div style={styles.twStats}>
                  <div style={styles.intelligenceListItem}>
                    <CheckCircle size={13} color="#4A8C5A" />
                    {fullyPaid} vendor{fullyPaid !== 1 ? 's' : ''} fully paid
                  </div>
                  <div style={styles.intelligenceListItem}>
                    <Clock size={13} color="#C6A75A" />
                    {openBalance} with open balance
                  </div>
                  <div style={{ ...styles.intelligenceListItem, color: '#9C7B3A', fontWeight: 700 }}>
                    <DollarSign size={13} color="#9C7B3A" />
                    {formatMoney(totals.balance)} remaining
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* B3: Upcoming Payments */}
        <div style={styles.intelligenceCard} data-testid="upcoming-payments">
          <div style={styles.intelligenceHeader}>
            <div style={{ ...styles.intelligenceIcon, ...styles.intelligenceIconUpcoming }}>
              <Wallet size={18} color="#3b82f6" />
            </div>
            <div>
              <div style={styles.intelligenceTitle}>Upcoming Payments</div>
              <div style={styles.intelligenceSub}>Next 30 Days</div>
            </div>
          </div>
          <div style={styles.intelligenceBody}>
            {upcomingPaymentVendors.length > 0 ? (
              <>
                <span style={styles.intelligenceHighlight}>{upcomingPaymentVendors.length}</span> payment{upcomingPaymentVendors.length !== 1 ? 's' : ''} due soon totaling{' '}
                <span style={styles.intelligenceHighlight}>
                  {formatMoney(upcomingPaymentVendors.reduce((sum, v) => sum + getVendorRemaining(v), 0))}
                </span>
              </>
            ) : (
              <>No payments due in the next 30 days</>
            )}
          </div>
          {upcomingPaymentVendors.length > 0 && (
            <div style={styles.intelligenceList}>
              {upcomingPaymentVendors.slice(0, 2).map((v, i) => (
                <div key={v.id || i} style={styles.intelligenceListItem}>
                  <Calendar size={12} color="#3b82f6" />
                  {v.company_name}: {formatMoney(getVendorRemaining(v))}
                </div>
              ))}
              {upcomingPaymentVendors.length > 2 && (
                <div style={{ ...styles.intelligenceListItem, color: '#5C5446', fontSize: 11 }}>
                  +{upcomingPaymentVendors.length - 2} more...
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══ C1-C3: ALERT BAR ══════════════════════════════════════════════════════ */}
      {!alertDismissed && urgentVendor && (
        <div 
          style={{ ...styles.alertBar, ...styles.alertBarUrgent }}
          data-testid="urgent-alert-bar"
        >
          <div style={{ ...styles.alertBarIcon, ...styles.alertBarIconUrgent }}>
            <AlertTriangle size={18} color="#dc2626" />
          </div>
          <div style={styles.alertBarContent}>
            <div style={styles.alertBarTitle}>
              Payment Due Soon: {urgentVendor.company_name}
            </div>
            <div style={styles.alertBarText}>
              {formatMoney(getVendorRemaining(urgentVendor))} balance due 
              {urgentVendor.balance_due_date && ` by ${formatShortDate(urgentVendor.balance_due_date)}`}
              . Click to review and log payment.
            </div>
          </div>
          <div style={styles.alertBarActions}>
            <button
              style={{ ...styles.alertBarBtn, ...styles.alertBarBtnPrimary }}
              onClick={() => handleLogPayment(urgentVendor)}
              data-testid="alert-log-payment-btn"
            >
              Log Payment
            </button>
            <button
              style={{ ...styles.alertBarBtn, ...styles.alertBarBtnSecondary }}
              onClick={handleDismissAlert}
              data-testid="alert-dismiss-btn"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ═══ D1-D7: SEARCH & FILTERS ════════════════════════════════════════════ */}
      <div style={styles.controlShell} data-testid="vendor-controls">
        <div style={styles.controlMetaRow}>
          <span style={styles.controlEyebrow}>Vendor Ledger</span>
          <span style={styles.controlMeta}>{sortedVendors.length} vendors</span>
        </div>

        <div style={styles.toolbar}>
          <div style={styles.searchWrapper}>
            <Search size={16} style={styles.searchIcon} />
            <input
              data-testid="vendor-search"
              type="text"
              placeholder="Search vendors by name, category, contact..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          <div style={styles.chipRow}>
            {['all', ...EXPERIENCE_GROUP_ORDER].map((cat) => (
              <div
                key={cat}
                data-testid={`category-chip-${cat}`}
                onClick={() => setCategoryFilter(cat)}
                style={{
                  ...styles.chip,
                  ...(categoryFilter === cat ? styles.chipActive : {}),
                }}
              >
                <span style={{
                  ...styles.chipText,
                  ...(categoryFilter === cat ? styles.chipTextActive : {}),
                }}>
                  {cat === 'all' ? 'All' : cat}
                </span>
              </div>
            ))}
            {/* Separator + Missing-contract toggle chip */}
            <div style={{ width: 1, height: 18, background: 'rgba(139,130,116,0.25)', margin: '0 4px', alignSelf: 'center' }} aria-hidden="true" />
            <div
              data-testid="missing-contract-chip"
              onClick={() => setMissingContractOnly(v => !v)}
              title={missingContractOnly ? 'Showing only vendors without a contract' : 'Filter to vendors missing a contract'}
              aria-pressed={missingContractOnly}
              style={{
                ...styles.chip,
                ...(missingContractOnly ? styles.chipActive : {}),
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Paperclip size={12} color={missingContractOnly ? '#2A2010' : '#5C5446'} />
              <span style={{
                ...styles.chipText,
                ...(missingContractOnly ? styles.chipTextActive : {}),
              }}>
                Missing contract
              </span>
            </div>
          </div>

          <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <div
              data-testid="vendor-sort-btn"
              onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
              style={styles.sortBtn}
            >
              <ArrowUpDown size={14} color="#5C5446" />
              <span style={styles.sortBtnLabel}>Sort:</span>
              <span style={styles.sortBtnText}>
                {SORT_OPTIONS.find(o => o.key === sortKey)?.label || 'Due Date'}
              </span>
              <ChevronDown size={14} color="#5C5446" />
            </div>
            {sortDropdownOpen && (
              <div style={styles.sortDropdown}>
                {SORT_OPTIONS.map((opt) => (
                  <div
                    key={opt.key}
                    onClick={() => { setSortKey(opt.key); setSortDropdownOpen(false); }}
                    style={{
                      ...styles.sortOption,
                      ...(sortKey === opt.key ? styles.sortOptionActive : {}),
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = C.statusWarningTint}
                    onMouseLeave={(e) => e.currentTarget.style.background = sortKey === opt.key ? C.statusWarningTint : 'transparent'}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <select
            data-testid="filter-category"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={styles.filterDropdown}
          >
            <option value="All">Category: All</option>
            {ROLE_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            data-testid="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={styles.filterDropdown}
          >
            <option value="All">Status: All</option>
            {VENDOR_STATUSES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select
            data-testid="filter-payment"
            value={filterPayment}
            onChange={(e) => setFilterPayment(e.target.value)}
            style={styles.filterDropdown}
          >
            <option value="All">Payment: All</option>
            {PAYMENT_STATUSES.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>

          <div style={{ marginLeft: 'auto' }}>
            <button
              data-testid="add-vendor-btn"
              onClick={handleAddVendor}
              style={styles.addBtn}
            >
              <Plus size={16} color="#FFFFFF" />
              <span style={styles.addBtnText}>Add Vendor</span>
            </button>
          </div>
        </div>
      </div>

      {/* ═══ VENDOR LEDGER WITH GROUPED TABLE (E1-E2) ═══════════════════════════ */}
      <div style={styles.ledgerShell} data-testid="vendor-ledger">
        <div style={styles.ledgerHeaderRow}>
          <span style={styles.ledgerEyebrow}>All Vendors</span>
          <div style={styles.ledgerStatRow}>
            <span style={styles.ledgerStat}>
              <b style={styles.ledgerStatValue}>{formatMoney(totals.contract)}</b>
              <span style={styles.ledgerStatLabel}>Contracted</span>
            </span>
            <span style={styles.ledgerStat}>
              <b style={{ ...styles.ledgerStatValue, color: '#3E7D52' }}>{formatMoney(totals.paid)}</b>
              <span style={styles.ledgerStatLabel}>Paid</span>
            </span>
            <span style={styles.ledgerStat}>
              <b style={{ ...styles.ledgerStatValue, color: '#9C7B3A' }}>{formatMoney(totals.balance)}</b>
              <span style={styles.ledgerStatLabel}>Due</span>
            </span>
          </div>
        </div>

        {/* Table Header */}
        <div style={styles.tableHeader}>
          <div style={{ width: 20 }} />
          <div style={{ ...styles.tableHeaderText, width: 230 }}>Vendor</div>
          <div style={{ ...styles.tableHeaderText, width: 130, textAlign: 'center' }}>Progress</div>
          <div style={{ ...styles.tableHeaderText, width: 78, textAlign: 'center' }}>Total</div>
          <div style={{ ...styles.tableHeaderText, width: 84, textAlign: 'center' }}>Due</div>
          <div style={{ ...styles.tableHeaderText, width: 64, textAlign: 'center' }}>Last $</div>
          <div style={{ ...styles.tableHeaderText, width: 68, textAlign: 'center' }}>Last Date</div>
          <div style={{ ...styles.tableHeaderText, width: 82, textAlign: 'center' }}>Balance</div>
          <div style={{ ...styles.tableHeaderText, width: 94, textAlign: 'center' }}>Status</div>
          <div style={{ ...styles.tableHeaderText, width: 50, textAlign: 'right' }}>Actions</div>
        </div>

        {/* E1-E2: Grouped Vendor Rows */}
        {loading ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyText}>Loading vendors...</div>
          </div>
        ) : sortedVendors.length === 0 ? (
          <div style={styles.emptyState}>
            <Building2 size={42} color="#5C5446" style={styles.emptyIcon} />
            <div style={styles.emptyText}>No vendors match the current filter.</div>
            <div style={styles.emptySubtext}>Add your first vendor to get started.</div>
          </div>
        ) : (
          groupedVendors.map((group, groupIndex) => {
            // E2: Calculate group aggregates
            const groupContract = group.items.reduce((sum, v) => sum + getVendorContract(v), 0);
            const groupPaid = group.items.reduce((sum, v) => sum + getVendorPaid(v), 0);
            const groupPct = groupContract > 0 ? Math.round((groupPaid / groupContract) * 100) : 0;
            const fillColor = groupPct >= 100 ? '#4A8C5A' : groupPct > 50 ? '#C6A75A' : '#EAB354';

            return (
              <div
                key={group.group}
                style={{
                  ...styles.groupShell,
                  ...(groupIndex === 0 ? styles.groupShellFirst : {}),
                }}
                data-testid={`vendor-group-${group.group}`}
              >
                {/* E2: Group Header */}
                <div style={styles.groupHeader}>
                  <span style={styles.groupTitle}>{group.group}</span>
                  <div style={styles.groupRight}>
                    {/* E2: Aggregate Progress Bar */}
                    <div style={styles.miniTrack}>
                      <div
                        style={{
                          ...styles.miniFill,
                          width: `${Math.min(groupPct, 100)}%`,
                          background: fillColor,
                        }}
                      />
                    </div>
                    <span style={styles.groupPct}>{groupPct}%</span>
                    <span style={styles.groupAmt}>{formatMoney(groupContract)}</span>
                  </div>
                </div>

                {/* Vendor Rows in Group */}
                {group.items.map((vendor, i) => renderVendorRow(vendor, i === 0))}
              </div>
            );
          })
        )}

        {/* K1-K3: Totals Row */}
        {vendors.length > 0 && (
          <div style={styles.totalsRow} data-testid="vendor-totals">
            <div style={{ width: 20 }} />
            <div style={styles.totalsLabel}>Totals</div>
            <div style={{ width: 130 }} />
            <div style={{ ...styles.totalsValue, width: 78, color: '#1F1C17' }}>
              {formatMoney(totals.contract)}
            </div>
            <div style={{ width: 84 }} />
            <div style={{ ...styles.totalsValue, width: 64, color: '#4A8C5A' }}>
              {formatMoney(totals.paid)}
            </div>
            <div style={{ width: 68 }} />
            <div style={{ ...styles.totalsValue, width: 82, color: '#C8783A' }}>
              -{formatMoney(totals.balance)}
            </div>
            <div style={{ width: 94 }} />
            <div style={{ width: 50 }} />
          </div>
        )}
      </div>

      {/* Add/Edit Vendor Modal */}
      {showModal && (
        <VendorModal 
          vendor={editingVendor} 
          onClose={handleModalClose} 
          onSave={handleVendorSaved} 
        />
      )}

      {/* G1-G13: Vendor Detail Drawer */}
      {showDrawer && drawerVendor && (
        <VendorDetailDrawer
          vendor={drawerVendor}
          eventDate={null} // Pass actual event date if available
          onClose={handleDrawerClose}
          onSave={handleDrawerSave}
          onSaveNow={handleDrawerSaveNow} // #3: explicit Save button flushes immediately
          savedSignal={drawerSavedSignal} // #92: persisted-only "Saved" confirmation
          onLogPayment={handleLogPayment}
          onReviewPayment={handleReviewPayment}
        />
      )}

      {/* ═══ I1-I8: LOG PAYMENT MODAL ═════════════════════════════════════════════ */}
      {showLogPayment && paymentVendor && (
        <LogPaymentModal
          vendor={paymentVendor}
          onClose={() => { setShowLogPayment(false); setPaymentVendor(null); }}
          onSubmit={handlePaymentSubmit}
        />
      )}

      {/* ═══ J1-J4: REVIEW PAYMENT MODAL ══════════════════════════════════════════ */}
      {showReviewPayment && paymentVendor && (
        <ReviewPaymentModal
          vendor={paymentVendor}
          onClose={() => { setShowReviewPayment(false); setPaymentVendor(null); }}
          onLogPayment={handleReviewToLog}
        />
      )}

      {/* ═══ MILO CONTRACT INTELLIGENCE — analyze → review → diff → apply → undo ═════ */}
      <ContractIntelligencePanel
        file={contractFile}
        onClose={() => setContractFile(null)}
        onApplied={() => { fetchVendors(); fetchStats(); if (onVendorChange) onVendorChange(); }}
      />

      {/* ═══ L1-L3: UNDO SNACKBAR ═════════════════════════════════════════════════ */}
      {showUndoSnackbar && undoData && (
        <div style={styles.undoSnackbar} data-testid="undo-snackbar">
          <span style={styles.undoText}>
            Changes saved to {undoData.vendor?.company_name}
          </span>
          <button 
            style={styles.undoBtn} 
            onClick={handleUndo}
            data-testid="undo-btn"
          >
            <Undo2 size={14} /> Undo
          </button>
          <button 
            style={styles.undoClose} 
            onClick={() => setShowUndoSnackbar(false)}
            data-testid="undo-dismiss-btn"
          >
            <X size={16} color="#5C5446" />
          </button>
        </div>
      )}

      {/* Hidden file input for contract upload — placed at root level for reliable ref access */}
      <input
        ref={contractFileInputRef}
        type="file"
        accept=".pdf,image/*"
        style={{ display: 'none' }}
        onChange={handleContractFileChange}
        data-testid="contract-file-input"
      />
    </div>
  );
};

// ─── Vendor Modal Component (H1-H9 Improvements) ───────────────────────────────

const VendorModal = ({ vendor, onClose, onSave }) => {
  const existingPc = vendor?.pricing_conditions || {};
  const [formData, setFormData] = useState({
    company_name: vendor?.company_name || '',
    contact_name: vendor?.contact_name || '',
    role_category: vendor?.role_category || 'Other',
    phone: vendor?.phone || '',
    email: vendor?.email || '',
    website: vendor?.website || '',
    address: vendor?.address || '',
    quote_total: vendor?.quote_total || 0,
    contract_total: vendor?.contract_total || 0,
    deposit_amount: vendor?.deposit_amount || 0,
    deposit_due_date: vendor?.deposit_due_date || '',
    paid_to_date: vendor?.paid_to_date || 0,
    balance_due_date: vendor?.balance_due_date || '',
    due_date: vendor?.due_date || '',
    vendor_status: vendor?.vendor_status || 'Considering',
    needs_attention: vendor?.needs_attention || false,
    needs_meal: vendor?.needs_meal || false,
    meal_count: vendor?.meal_count || 0,
    notes: vendor?.notes || '',
  });
  const [showAdvanced, setShowAdvanced] = useState(!!vendor);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  // VEN-01: duplicate prompt (opened when POST /vendors returns 409 POSSIBLE_DUPLICATE_VENDOR)
  const [dupPrompt, setDupPrompt] = useState(null);   // { candidates, message } | null
  const [keepReason, setKeepReason] = useState('');

  // Pricing conditions state
  const [showPricing, setShowPricing] = useState(!!(existingPc.adult_rate != null));
  const [pcAdultRate, setPcAdultRate] = useState(existingPc.adult_rate != null ? String(existingPc.adult_rate) : '');
  const [pcAdultOp, setPcAdultOp] = useState(existingPc.adult_operator || '>=');
  const [pcAdultThresh, setPcAdultThresh] = useState(existingPc.adult_threshold != null ? String(existingPc.adult_threshold) : '');
  const [pcAdultField, setPcAdultField] = useState(existingPc.adult_count_field || 'adult_count');
  const [pcChildRate, setPcChildRate] = useState(existingPc.child_rate != null ? String(existingPc.child_rate) : '');
  const [pcChildThresh, setPcChildThresh] = useState(existingPc.child_threshold != null ? String(existingPc.child_threshold) : '');
  // Optional per-venue condition: gate the child rate behind a minimum adult count. Default OFF.
  const [pcChildCondition, setPcChildCondition] = useState(existingPc.child_threshold != null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const buildPayload = (extra = {}) => {
    // Pricing: FLAT per-head rates by default (no condition required to save).
    // The child rate can OPTIONALLY be gated behind a per-venue condition
    // (e.g. only applies once adults reach a minimum); default is no condition.
    let pricing_conditions = null;
    // Effective rates: prefer the typed value, else fall back to the EXISTING saved rate.
    // This stops a partial edit (e.g. just ticking the child condition) from silently
    // dropping the whole pricing block when a rate field reads empty.
    const effAdult = (pcAdultRate !== '' && pcAdultRate != null) ? parseFloat(pcAdultRate)
      : (existingPc.adult_rate != null ? Number(existingPc.adult_rate) : null);
    const effChild = (pcChildRate !== '' && pcChildRate != null) ? parseFloat(pcChildRate)
      : (existingPc.child_rate != null ? Number(existingPc.child_rate) : null);
    if (showPricing && (effAdult != null || effChild != null)) {
      pricing_conditions = {};
      if (effAdult != null) {
        pricing_conditions.adult_rate = effAdult;
        pricing_conditions.adult_count_field = 'adult_count';
      }
      // Legacy adult condition support (only if a threshold was explicitly set)
      if (pcAdultThresh) {
        pricing_conditions.adult_operator = pcAdultOp;
        pricing_conditions.adult_threshold = parseFloat(pcAdultThresh);
        pricing_conditions.adult_count_field = pcAdultField;
      }
      if (effChild != null) {
        pricing_conditions.child_rate = effChild;
        pricing_conditions.child_count_field = 'adult_count';
        // Optional per-venue condition: child rate only at >= N adults.
        if (pcChildCondition) {
          pricing_conditions.child_operator = '>=';
          pricing_conditions.child_threshold = parseFloat(pcChildThresh) || 75;
          pricing_conditions.child_count_field = 'adult_count';
        }
      }
    }
    return { ...formData, pricing_conditions, ...extra };
  };

  // VEN-01: create a new vendor; on a 409 POSSIBLE_DUPLICATE_VENDOR, open the duplicate prompt
  // (attach / merge / create-new / keep-separate) instead of failing.
  const submitNew = async (extra = {}) => {
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/vendors`, buildPayload(extra));
      toast.success('Vendor added');
      onSave();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && detail.code === 'POSSIBLE_DUPLICATE_VENDOR') {
        setDupPrompt({ candidates: detail.candidates || [], message: detail.message });
      } else {
        const msg = typeof detail === 'string' ? detail : 'Error saving vendor';
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }
    setError('');
    if (vendor) {
      setSaving(true);
      try {
        await axios.patch(`${API_URL}/api/vendors/${vendor.id}`, buildPayload());
        toast.success('Vendor updated');
        onSave();
      } catch (err) {
        const msg = err.response?.data?.detail;
        setError(typeof msg === 'string' ? msg : 'Error saving vendor');
        toast.error('Error saving vendor');
      } finally {
        setSaving(false);
      }
    } else {
      await submitNew();
    }
  };

  // VEN-01 duplicate-prompt resolvers
  const dupCancel = () => setDupPrompt(null);
  const dupCreateNew = async () => { setDupPrompt(null); await submitNew({ duplicate_resolution: 'create_new' }); };
  const dupKeepSeparate = async () => {
    const reason = (keepReason || '').trim();
    setDupPrompt(null);
    await submitNew({ duplicate_resolution: 'keep_separate', keep_separate_reason: reason });
  };
  const dupUseExisting = () => {
    const c = dupPrompt?.candidates?.[0];
    setDupPrompt(null);
    toast.success(`Keeping existing "${c?.company_name || 'vendor'}" — no duplicate created`);
    onClose();
  };
  const dupMergeInto = async () => {
    const c = dupPrompt?.candidates?.[0];
    if (!c) { setDupPrompt(null); return; }
    setSaving(true);
    try {
      const resp = await axios.post(`${API_URL}/api/vendors`, buildPayload({ duplicate_resolution: 'create_new' }));
      const newId = resp.data?.id;
      await axios.post(`${API_URL}/api/vendors/${c.vendor_id}/merge`, { duplicate_id: newId, reason: (keepReason || '').trim() || 'Merged at create' });
      toast.success(`Merged into existing "${c.company_name}"`);
      setDupPrompt(null);
      onSave();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Merge failed');
    } finally {
      setSaving(false);
    }
  };

  // H9: Suggested payment timeline
  const timeline = useMemo(() => {
    if (!formData.contract_total) return null;
    const deposit = formData.deposit_amount || Math.round(formData.contract_total * 0.5);
    const balance = formData.contract_total - deposit;
    return { deposit, balance };
  }, [formData.contract_total, formData.deposit_amount]);

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    card: {
      background: C.bgCard,
      borderRadius: 22,
      width: '100%',
      maxWidth: 680,
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 40px 80px rgba(0,0,0,0.2)',
      border: '1px solid rgba(198,167,90,0.22)',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 24px',
      borderBottom: '1px solid rgba(198,167,90,0.18)',
    },
    title: {
      margin: 0,
      fontSize: 18,
      fontWeight: 700,
      color: '#1F1C17',
    },
    subtitle: {
      fontSize: 12,
      color: '#5C5446',
      marginTop: 2,
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 4,
    },
    form: {
      padding: 24,
    },
    error: {
      background: 'rgba(220,53,69,0.1)',
      color: '#dc2626',
      padding: '10px 14px',
      borderRadius: 12,
      marginBottom: 16,
      fontSize: 13,
    },
    section: {
      marginBottom: 24,
    },
    sectionTitle: {
      fontSize: 10,
      fontWeight: 700,
      color: '#4F4838',
      marginBottom: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 12,
    },
    gridWide: {
      gridColumn: '1 / -1',
    },
    fieldLabel: {
      display: 'block',
      fontSize: 11,
      color: '#4F4838',
      marginBottom: 4,
      fontWeight: 600,
    },
    fieldInput: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid rgba(198,167,90,0.22)',
      borderRadius: 10,
      fontSize: 13,
      background: C.bgCard,
      color: '#1F1C17',
    },
    fieldSelect: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid rgba(198,167,90,0.22)',
      borderRadius: 10,
      fontSize: 13,
      background: C.bgCard,
      color: '#1F1C17',
    },
    toggleBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: 12,
      fontWeight: 600,
      color: '#4F4838',
      padding: 0,
      marginBottom: showAdvanced ? 16 : 0,
    },
    actions: {
      display: 'flex',
      gap: 12,
      justifyContent: 'flex-end',
      marginTop: 24,
      paddingTop: 16,
      borderTop: '1px solid rgba(198,167,90,0.18)',
    },
    cancelBtn: {
      padding: '10px 20px',
      background: 'transparent',
      border: '1px solid rgba(198,167,90,0.22)',
      borderRadius: 12,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
      color: '#6F685C',
    },
    saveBtn: {
      padding: '10px 24px',
      background: '#C6A75A',
      border: 'none',
      borderRadius: 12,
      fontSize: 13,
      fontWeight: 700,
      cursor: 'pointer',
      color: '#FFFFFF',
      opacity: saving ? 0.7 : 1,
    },
    // H2: Grouped Category Picker styles
    categoryBtn: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid rgba(198,167,90,0.22)',
      borderRadius: 10,
      fontSize: 13,
      background: C.bgCard,
      color: '#1F1C17',
      textAlign: 'left',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    categoryDropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: 4,
      background: C.bgCard,
      border: '1px solid rgba(198,167,90,0.22)',
      borderRadius: 12,
      boxShadow: '0px 20px 40px rgba(18,18,18,0.12)',
      zIndex: 100,
      maxHeight: 280,
      overflowY: 'auto',
    },
    categoryGroup: {
      padding: '8px 12px 4px',
      fontSize: 9,
      fontWeight: 700,
      color: '#5C5446',
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      borderTop: '1px solid rgba(198,167,90,0.12)',
    },
    categoryGroupFirst: {
      borderTop: 'none',
    },
    categoryOption: {
      padding: '8px 12px',
      fontSize: 13,
      color: '#463F34',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    // H8: Status Chips styles
    statusChips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
    },
    statusChip: {
      padding: '8px 14px',
      borderRadius: 999,
      border: '1px solid rgba(198,167,90,0.22)',
      background: C.bgCard,
      cursor: 'pointer',
      fontSize: 11,
      fontWeight: 600,
      transition: 'all 0.15s',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    statusChipActive: {
      borderColor: '#C6A75A',
      background: C.statusWarningTint,
    },
    // H9: Timeline Preview styles
    timelinePreview: {
      padding: '14px 16px',
      background: C.bgTableDivider,
      borderRadius: 12,
      border: '1px solid rgba(198,167,90,0.12)',
      marginTop: 16,
    },
    timelineTitle: {
      fontSize: 10,
      fontWeight: 700,
      color: '#4F4838',
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      marginBottom: 10,
    },
    timelineRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '6px 0',
    },
    timelineLabel: {
      fontSize: 12,
      color: '#6F685C',
    },
    timelineValue: {
      fontSize: 13,
      fontWeight: 700,
      color: '#1F1C17',
    },
    timelineValueGreen: {
      color: '#4A8C5A',
    },
    timelineValueGold: {
      color: '#C6A75A',
    },
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      {dupPrompt && (
        <div onClick={(e) => e.stopPropagation()} data-testid="vendor-dup-prompt"
          style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: C.bgCard, borderRadius: 20, maxWidth: 560, width: '100%', maxHeight: '88vh', overflow: 'auto', border: `1px solid ${C.borderStrong}`, boxShadow: '0 40px 80px rgba(0,0,0,0.25)', padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: C.statusWarning, display: 'inline-block' }} />
              <h3 style={{ margin: 0, fontFamily: 'Playfair Display, serif', fontSize: 20, color: C.textPrimary }}>Possible duplicate vendor</h3>
            </div>
            <p style={{ margin: '0 0 16px', color: C.textSecondary, fontSize: 14, lineHeight: 1.5 }}>
              {dupPrompt.message || 'One or more existing vendors look similar. Choose how to proceed.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
              {(dupPrompt.candidates || []).map((c) => (
                <div key={c.vendor_id} style={{ border: `1px solid ${C.borderMedium}`, borderRadius: 12, padding: '12px 14px', background: C.bgSectionHeader }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                    <strong style={{ color: C.textPrimary, fontSize: 15 }}>{c.company_name}</strong>
                    <span style={{ color: C.textMuted, fontSize: 13 }}>{c.role_category}{c.contract_total != null ? ` · $${Number(c.contract_total).toLocaleString()}` : ''}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {(c.matched_fields || []).map((f) => (
                      <span key={f} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: C.accentTintMedium, color: '#8A7847' }}>matches {String(f).replace('_', ' ')}</span>
                    ))}
                    {c.role_differs && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: C.statusWarningTint, color: '#9A6B00' }}>different role</span>}
                  </div>
                </div>
              ))}
            </div>
            <label style={{ display: 'block', fontSize: 12, color: C.textMuted, marginBottom: 6 }}>If keeping separate, note why (optional)</label>
            <input value={keepReason} onChange={(e) => setKeepReason(e.target.value)} placeholder="e.g. genuinely two different services"
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 10, border: `1px solid ${C.borderInput}`, marginBottom: 18, fontSize: 14 }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
              <button type="button" onClick={dupCancel} disabled={saving} style={{ padding: '9px 12px', borderRadius: 999, border: 'none', background: 'transparent', color: C.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={dupUseExisting} disabled={saving} style={{ padding: '9px 16px', borderRadius: 999, border: `1px solid ${C.borderMedium}`, background: C.bgCard, color: C.textPrimary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Use existing</button>
              <button type="button" onClick={dupKeepSeparate} disabled={saving} style={{ padding: '9px 16px', borderRadius: 999, border: `1px solid ${C.borderMedium}`, background: C.bgCard, color: C.textPrimary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Keep separate</button>
              <button type="button" onClick={dupCreateNew} disabled={saving} style={{ padding: '9px 16px', borderRadius: 999, border: `1px solid ${C.borderMedium}`, background: C.bgCard, color: C.textPrimary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Create new anyway</button>
              <button type="button" onClick={dupMergeInto} disabled={saving} style={{ padding: '9px 18px', borderRadius: 999, border: `1px solid ${C.btnPrimaryBorder}`, background: C.btnPrimaryBg, color: C.btnPrimaryText, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Merge into existing</button>
            </div>
          </div>
        </div>
      )}
      <div style={modalStyles.card} onClick={(e) => e.stopPropagation()} data-testid="vendor-modal">
        <div style={modalStyles.header}>
          <div>
            <h2 style={modalStyles.title}>{vendor ? 'Edit Vendor' : 'Add Vendor'}</h2>
            <div style={modalStyles.subtitle}>
              {vendor ? 'Update vendor details' : 'Add a new partner without leaving the command center.'}
            </div>
          </div>
          <button onClick={onClose} style={modalStyles.closeBtn}>
            <X size={20} color="#5C5446" />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={modalStyles.form}>
          {error && <div style={modalStyles.error}>{error}</div>}

          {/* H1: Company Name Field */}
          <div style={modalStyles.section}>
            <div style={modalStyles.sectionTitle}>Basic Information</div>
            <div style={modalStyles.grid}>
              <div style={modalStyles.gridWide}>
                <label style={modalStyles.fieldLabel}>Company Name *</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="Elegant Catering Co."
                  style={modalStyles.fieldInput}
                  required
                  data-testid="vendor-company-name"
                />
              </div>
              
              {/* H2: Grouped Category Selector */}
              <div style={{ position: 'relative' }}>
                <label style={modalStyles.fieldLabel}>Category</label>
                <button
                  type="button"
                  onClick={() => setShowCategoryPicker(!showCategoryPicker)}
                  style={modalStyles.categoryBtn}
                  data-testid="vendor-category-picker"
                >
                  <span>{formData.role_category || 'Select category'}</span>
                  <ChevronDown size={16} color="#5C5446" />
                </button>
                {showCategoryPicker && (
                  <div style={modalStyles.categoryDropdown}>
                    {VENDOR_CATEGORY_GROUPS.map((group, gi) => (
                      <div key={group.label}>
                        <div style={{
                          ...modalStyles.categoryGroup,
                          ...(gi === 0 ? modalStyles.categoryGroupFirst : {}),
                        }}>
                          {group.label}
                        </div>
                        {group.options.map((opt) => (
                          <div
                            key={opt}
                            onClick={() => { handleChange('role_category', opt); setShowCategoryPicker(false); }}
                            style={modalStyles.categoryOption}
                            onMouseEnter={(e) => e.currentTarget.style.background = C.statusWarningTint}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            {opt}
                            {formData.role_category === opt && <Check size={16} color="#C6A75A" />}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* H3: Contact Name */}
              <div>
                <label style={modalStyles.fieldLabel}>Contact Name</label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => handleChange('contact_name', e.target.value)}
                  placeholder="Primary contact"
                  style={modalStyles.fieldInput}
                />
              </div>
              
              {/* H3: Email */}
              <div>
                <label style={modalStyles.fieldLabel}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="vendor@example.com"
                  style={modalStyles.fieldInput}
                />
              </div>
              
              {/* H3: Phone */}
              <div>
                <label style={modalStyles.fieldLabel}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', formatPhoneInput(e.target.value))}
                  placeholder="(555) 000-0000"
                  style={modalStyles.fieldInput}
                />
              </div>
            </div>
          </div>

          {/* H8: Status Chips */}
          <div style={modalStyles.section}>
            <div style={modalStyles.sectionTitle}>Status</div>
            <div style={modalStyles.statusChips}>
              {VENDOR_STATUSES.map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => handleChange('vendor_status', status)}
                  style={{
                    ...modalStyles.statusChip,
                    ...(formData.vendor_status === status ? modalStyles.statusChipActive : {}),
                  }}
                  data-testid={`status-chip-${status}`}
                >
                  {formData.vendor_status === status && <Check size={14} color="#C6A75A" />}
                  {status}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={modalStyles.toggleBtn}
          >
            {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            Financial & Additional Details
          </button>

          {showAdvanced && (
            <>
              {/* H6: Contract/Paid/Deposit Fields */}
              <div style={modalStyles.section}>
                <div style={modalStyles.sectionTitle}>Financials</div>
                <div style={{ ...modalStyles.grid, gridTemplateColumns: '1fr 1fr 1fr' }}>
                  <div>
                    <label style={modalStyles.fieldLabel}>Contract Total</label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <span style={{ position: 'absolute', left: 10, fontSize: 13, color: '#5C5446', pointerEvents: 'none' }}>$</span>
                      <input
                        type="number"
                        value={formData.contract_total || ''}
                        onChange={(e) => handleChange('contract_total', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        placeholder="0.00"
                        style={{ ...modalStyles.fieldInput, paddingLeft: 24 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={modalStyles.fieldLabel}>Paid to Date</label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <span style={{ position: 'absolute', left: 10, fontSize: 13, color: '#5C5446', pointerEvents: 'none' }}>$</span>
                      <input
                        type="number"
                        value={formData.paid_to_date || ''}
                        onChange={(e) => handleChange('paid_to_date', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        placeholder="0.00"
                        style={{ ...modalStyles.fieldInput, paddingLeft: 24 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={modalStyles.fieldLabel}>Deposit Amount</label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <span style={{ position: 'absolute', left: 10, fontSize: 13, color: '#5C5446', pointerEvents: 'none' }}>$</span>
                      <input
                        type="number"
                        value={formData.deposit_amount || ''}
                        onChange={(e) => handleChange('deposit_amount', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        placeholder="0.00"
                        style={{ ...modalStyles.fieldInput, paddingLeft: 24 }}
                      />
                    </div>
                  </div>
                  
                  {/* H7: Due Date Fields */}
                  <div>
                    <label style={modalStyles.fieldLabel}>Deposit Due</label>
                    <input
                      type="date"
                      value={formData.deposit_due_date}
                      onChange={(e) => handleChange('deposit_due_date', e.target.value)}
                      style={modalStyles.fieldInput}
                    />
                  </div>
                  <div>
                    <label style={modalStyles.fieldLabel}>Balance Due Date</label>
                    <input
                      type="date"
                      value={formData.balance_due_date || formData.due_date}
                      onChange={(e) => handleChange('balance_due_date', e.target.value)}
                      style={modalStyles.fieldInput}
                    />
                  </div>
                </div>

                {/* H9: Timeline Preview */}
                {formData.contract_total > 0 && timeline && (
                  <div style={modalStyles.timelinePreview} data-testid="payment-timeline-preview">
                    <div style={modalStyles.timelineTitle}>Suggested Payment Timeline</div>
                    <div style={modalStyles.timelineRow}>
                      <span style={modalStyles.timelineLabel}>Deposit (50%)</span>
                      <span style={{ ...modalStyles.timelineValue, ...modalStyles.timelineValueGold }}>
                        {formatMoney(timeline.deposit)}
                      </span>
                    </div>
                    <div style={modalStyles.timelineRow}>
                      <span style={modalStyles.timelineLabel}>Balance</span>
                      <span style={{ ...modalStyles.timelineValue }}>
                        {formatMoney(timeline.balance)}
                      </span>
                    </div>
                    <div style={{ ...modalStyles.timelineRow, borderTop: '1px solid rgba(198,167,90,0.12)', marginTop: 6, paddingTop: 10 }}>
                      <span style={modalStyles.timelineLabel}>Total Contract</span>
                      <span style={{ ...modalStyles.timelineValue, ...modalStyles.timelineValueGreen }}>
                        {formatMoney(formData.contract_total)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* H4-H5: Address & Website */}
              <div style={modalStyles.section}>
                <div style={modalStyles.sectionTitle}>Additional Info</div>
                <div style={modalStyles.grid}>
                  <div>
                    <label style={modalStyles.fieldLabel}>Website</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleChange('website', e.target.value)}
                      placeholder="https://vendor.com"
                      style={modalStyles.fieldInput}
                    />
                  </div>
                  <div>
                    <label style={modalStyles.fieldLabel}>Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      placeholder="123 Main St, City, State"
                      style={modalStyles.fieldInput}
                    />
                  </div>
                </div>
              </div>

              <div style={modalStyles.section}>
                <div style={modalStyles.sectionTitle}>Event Impact</div>
                <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1F1C17', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.needs_meal}
                      onChange={(e) => handleChange('needs_meal', e.target.checked)}
                    />
                    <Utensils size={14} color="#5C5446" />
                    Needs Meal
                  </label>
                  {formData.needs_meal && (
                    <div style={{ width: 80 }}>
                      <input
                        type="number"
                        value={formData.meal_count || ''}
                        onChange={(e) => handleChange('meal_count', e.target.value === '' ? 0 : (parseInt(e.target.value) || 0))}
                        placeholder="Count"
                        style={{ ...modalStyles.fieldInput, padding: '6px 10px' }}
                      />
                    </div>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1F1C17', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.needs_attention}
                      onChange={(e) => handleChange('needs_attention', e.target.checked)}
                    />
                    <AlertCircle size={14} color="#dc2626" />
                    Needs Attention
                  </label>
                </div>
              </div>

              <div style={modalStyles.section}>
                <label style={modalStyles.fieldLabel}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={3}
                  placeholder="Additional notes about this vendor..."
                  style={{ ...modalStyles.fieldInput, resize: 'vertical' }}
                />
              </div>

              {/* Event Pricing Conditions */}
              <div style={{ background: 'rgba(198,167,90,0.06)', border: '1px solid rgba(198,167,90,0.18)', borderRadius: 12, overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={() => setShowPricing(p => !p)}
                  style={{ width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer' }}
                  data-testid="toggle-pricing-conditions"
                >
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#C6A75A' }}>Event Pricing Conditions</span>
                  <span style={{ fontSize: 11, color: '#5C5446' }}>{showPricing ? '▲' : '▼'}</span>
                </button>
                {showPricing && (
                  <div style={{ padding: '0 16px 16px' }}>
                    <div style={{ fontSize: 11, color: '#5C5446', marginBottom: 12, lineHeight: 1.5 }}>
                      Per-head rates for this vendor. They aggregate into the Dashboard plate-cost KPI and mirror to Settings → Plate Cost.
                    </div>
                    {/* Adult rate (flat) */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#4F4838', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Adult Rate (per head)</div>
                      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                        <span style={{ position: 'absolute', left: 10, fontSize: 13, color: '#5C5446', pointerEvents: 'none' }}>$</span>
                        <input value={pcAdultRate} onChange={e => setPcAdultRate(e.target.value)} placeholder="120" type="number" style={{ ...modalStyles.fieldInput, width: 120, paddingLeft: 24 }} data-testid="vendor-adult-rate" />
                      </div>
                    </div>
                    {/* Child rate (flat) + optional condition */}
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#4F4838', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Child Rate (per head)</div>
                      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                        <span style={{ position: 'absolute', left: 10, fontSize: 13, color: '#5C5446', pointerEvents: 'none' }}>$</span>
                        <input value={pcChildRate} onChange={e => setPcChildRate(e.target.value)} placeholder="60" type="number" style={{ ...modalStyles.fieldInput, width: 120, paddingLeft: 24 }} data-testid="vendor-child-rate" />
                      </div>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#463F34', cursor: 'pointer', marginTop: 10, lineHeight: 1.4 }} data-testid="vendor-child-condition-toggle">
                        <input type="checkbox" checked={pcChildCondition} onChange={e => setPcChildCondition(e.target.checked)} style={{ marginTop: 2 }} />
                        <span>Apply the child rate <strong>only when</strong> adults reach a minimum — otherwise children are charged the adult rate. <em>(This depends on the venue; leave off for a flat child rate.)</em></span>
                      </label>
                      {pcChildCondition && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, paddingLeft: 26 }}>
                          <span style={{ fontSize: 12, color: '#463F34' }}>Minimum adults:</span>
                          <input value={pcChildThresh} onChange={e => setPcChildThresh(e.target.value)} placeholder="75" type="number" style={{ ...modalStyles.fieldInput, width: 80 }} data-testid="vendor-child-threshold" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div style={modalStyles.actions}>
            <button type="button" onClick={onClose} style={modalStyles.cancelBtn}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={modalStyles.saveBtn}>
              {saving ? 'Saving...' : (vendor ? 'Update Vendor' : 'Save Vendor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorsTab;
